import { BehaviorSubject } from 'rxjs';
import { File } from '@ionic-native/file';
import { Injectable, forwardRef, Inject } from '@angular/core';
import { Events, ModalController, Platform } from 'ionic-angular';
import { HttpProvider, RefreshUserDetails } from '../http/http';
import { AuthMapper, AuthProvider } from '../authorization';
import { BiometricLoginFailureReason } from '../authorization/auth';
import { RmtPopupComponent } from '../../components/rmt-popup/rmt-popup';
import { SegmentLocationNames, AnalyticsEventType, CarouselCTAEntryPoint } from '../../model/types/analytics.const';
import { LoginProvider } from '../myria/login-provider';
import { PermissionProvider } from '../myria/permission-provider';
import { DELIVERY_TYPE, EVENTS_NAME, FORGOT_PASSWORD_RET_VAL, LOGIN_ERRORS_NEW_TOKEN, OTP_ACTIONS, OTP_REGISTRATION_RET_VAL } from '../myria/app-variables';
import { ResourceProvider } from '../resource/resource';
import { LoggerEvent, LoggerProvider } from '../logger/logger';
import { APP_CONFIG, Env } from '../../environment';
import { NativeProvider } from '../native/native';
import { XeCaptchaComponent } from '../../components/xe-captcha/xe-captcha';
import { ScriptProvider } from '../script/script';
import { SegmentProvider } from '../analytics/segment';
import { VueBranchProvider } from './vue-branch';
import { LoadingProvider } from '../loading/loading';
import { BridgeMessageFromType, BridgeMessageToType, BridgeNativeType, BridgeNativeModalTarget, BridgeAPIType, BridgeBackData, BridgeBankDepositRoute, BridgeBankDepositEntry, BridgeBankDepositCompleteData, BridgeWelcomeCountrySelectedData, BridgeRedirectFullRegistrationData, BridgePartnerCheckoutRequest } from '../../model/types/bridge-types';
import { StorageProvider } from '../storage/storage';
import { StorageType } from '../../model/types/storage-type';
import { FeatureFlagTypes, UtilsProvider } from '../myria/utils-provider';
import { LANDING_PAGE_AB_TEST_VARIANTS } from '../../model/types/feature-flag-types';
import { RouteProvider } from '../route/route';
import { TranslateService } from '@ngx-translate/core';
import { VueBridgeContainerHelpers, TransitionType, VueBridgeState } from './vue-helpers';
import { KeyboardProvider } from '../keyboard/keyboard';
import { RegisterProvider } from '../register/register';
import { DeeplinkProvider } from '../marketing/deeplink';
import { WalletProvider } from '../wallet/wallet';
import { OnfidoProvider } from '../myria/onfido-provider';
import { ContactsProvider } from '../contacts/contacts';
import { CustomerProvider } from '../customer/customer';
import { IterableInAppProvider } from '../marketing/iterable-inapp';
import { AppVersionProvider } from '../app-version/app-version';
import { PaymentProvider } from '../payment/payment';
import { PlaidProvider } from '../plaid/plaid';
import { SendFlowProvider } from '../send-flow/send-flow';
import { HomeScreenProvider } from '../homescreen/homescreen';
import { RecipientProvider } from '../recipient/recipient';
import { OrderProvider } from '../order/order';
import { ReceiptProvider } from '../receipt/receipt';
import { PartnerCheckoutProvider } from '../partner-checkout/partner-checkout';
import { FileOpener } from '@ionic-native/file-opener';
import { SocialSharing } from '@ionic-native/social-sharing';
import { PaymentMethodType } from '../../model/types/payment-method-type';
import { BankAccountAddAllowed } from '../../model/types/bank-account-verification-status';

type VueNavigateData = {
  selectedBalanceId?: number;
  selectedCardId?: number;
  selectedActivityId?: number;
  countryFrom?: string;
  countryTo?: string;
  biometricType?: 'faceId' | 'touchId' | 'fingerprint';
  biometricsAvailable?: boolean;
  biometricsUnavailableReason?: BiometricLoginFailureReason;
  username?: string;
  firstName?: string;
  replace?: boolean;
  bankDeposit?: BridgeBankDepositEntry;
  query?: { [key: string]: string };
}

/** Outcome of a relogin shown in RMT5: finished, abandoned, or handed back to the native modal. */
export type VueReloginResult = boolean | 'native';

@Injectable()
export class VueProvider {
  private static readonly ACCERTIFY_PULL_TIMEOUT_MS = 4000;
  private static readonly BRIDGE_HTTP_BUDGET_MS = 55000;
  private static readonly POST_LOGIN_VUE_HOMES = ['RMT5Home', 'WalletHome', 'WalletOnboarding'];

  authProvider: AuthProvider;

  initialized = false;
  startTime = new Date();
  connectionAttempts = 0;
  maxConnectionAttempts = 1;
  connectionTimeout = 60000;
  retryDelay = 2000;
  resourcesReadyTimeout = 25000;

  connectUrl: string;
  origin;
  container;
  iframe;
  readyPromise;
  readyResolve;
  navigatePromise;
  navigateResolve;
  navigateDisabled = false;
  transitionPromise;
  transitionResolve;
  // last slide-in of the container; see waitForBridgeCover().
  private bridgeCoverPromise: Promise<void> | null = null;
  messageHandler;
  connectionTimeoutId;
  isConnected = false;
  disabled = false;
  handlingLoginComplete = false;
  autoHide: boolean = false;
  isSendMoneyFromBridge: boolean = false;
  sendMoneyReturnRoute: string = 'WalletHome';
  bridgeVisible$ = new BehaviorSubject<boolean>(false);
  bankDepositCompleteCallback: ((data: BridgeBankDepositCompleteData) => Promise<void> | void) | null = null;
  vueVisibilitySeq = 0;
  elevateIframe = false;
  private debugMenuOpen = false;
  restoreVueRouteAfterBankDeposit: string | null = null;
  onHardwareBackUnhandled: (() => void) | null = null;
  pendingPostLoginCallback: (() => void | Promise<void>) | null = null;
  pendingReloginResolve: ((result: VueReloginResult) => void) | null = null;
  biometricLoginScanInProgress = false;
  pendingLoginUsername: string;
  pendingLoginPassword: string;
  private pendingEmailOtp: { referenceValue: string, securityCode: string } = null;
  pendingSigninResult: any;
  // Signin held while RMT5 drives /onboarding/*; consumed on onboarding-complete.
  private pendingOnboardingSignin: any = null;
  private pendingOnboardingLocation: SegmentLocationNames = null;
  // Guards "RMT5 never started onboarding" (flag/bundle mismatch), not a slow user.
  private onboardingWatchdogTimer: any = null;
  private readonly onboardingWatchdogMs = 120000;
  // Under RMT5's own bridge timeout, so a wedged enable surfaces as an actionable error.
  private readonly biometricsSaveTimeoutMs = 45000;
  private onboardingFinalizeInFlight = false;
  currentVueRoute: string = '';
  private version: string = null;
  // 3DS Songbird bridge scope: enables the bridge TRANSPORT (init, connection,
  // postMessage) for the CyberSource/Songbird 3DS authorization flow only,
  // independent of the general rmt5 bridge flag. `disabled` keeps meaning
  // "RMT5 UI bridge off" — navigation/UI flows are unaffected by this scope.
  private songbird3dsBridgeEnabled = false;
  // Late-bound by Songbird3dsBridgeProvider (which owns the 3DS flow) so this
  // provider never imports it — avoids a DI cycle, same pattern as UtilsProvider.
  private songbird3dsBridge: { handleResult(message: any): void; handleReady(message: any): void; handleEnrollmentRequest(message: any): Promise<void> } | null = null;

  registerSongbird3dsBridge(handler: { handleResult(message: any): void; handleReady(message: any): void; handleEnrollmentRequest(message: any): Promise<void> }) {
    this.songbird3dsBridge = handler;
  }

  private isBridgeTransportAllowed(): boolean {
    return !this.disabled || this.songbird3dsBridgeEnabled;
  }

  /**
   * Enable the 3DS Songbird transport scope: the CyberSource/Songbird 3DS flow
   * may use the bridge transport even when the general rmt5 flag keeps the UI
   * bridge disabled. The POLICY decision to bypass the flag belongs to
   * Songbird3dsBridgeProvider — this method only flips the transport gate.
   */
  enableSongbird3dsScope() {
    if (this.songbird3dsBridgeEnabled) {
      return;
    }
    if (this.disabled) {
      LoggerProvider.info(this, 'Vue bridge disabled by rmt5 flag - enabling 3DS Songbird bridge scope');
    }
    this.songbird3dsBridgeEnabled = true;
  }

  constructor(
    private platform: Platform,
    private loginProvider: LoginProvider,
    private httpProvider: HttpProvider,
    private nativeProvider: NativeProvider,
    private segmentProvider: SegmentProvider,
    private loadingProvider: LoadingProvider,
    private analytics: SegmentProvider,
    private resourceProvider: ResourceProvider,
    private storageProvider: StorageProvider,
    private utilsProvider: UtilsProvider,
    private routeProvider: RouteProvider,
    private scriptProvider: ScriptProvider,
    private translate: TranslateService,
    private keyboardProvider: KeyboardProvider,
    private registerProvider: RegisterProvider,
    private customerProvider: CustomerProvider,
    private deeplinkProvider: DeeplinkProvider,
    private walletProvider: WalletProvider,
    @Inject(forwardRef(() => OnfidoProvider)) private onfidoProvider: OnfidoProvider,
    private contactsProvider: ContactsProvider,
    private iterableInAppProvider: IterableInAppProvider,
    private appVersionProvider: AppVersionProvider,
    private paymentProvider: PaymentProvider,
    private plaidProvider: PlaidProvider,
    private sendFlowProvider: SendFlowProvider,
    private homescreenProvider: HomeScreenProvider,
    private recipientProvider: RecipientProvider,
    private orderProvider: OrderProvider,
    private receiptProvider: ReceiptProvider,
    private file: File,
    private fileOpener: FileOpener,
    private socialSharing: SocialSharing,
    private modalCtrl: ModalController,
    private vueBranchProvider: VueBranchProvider,
    private events: Events,
    private partnerCheckoutProvider: PartnerCheckoutProvider,
  ) {
    this.deeplinkProvider.referralChanges.subscribe(() => this.notifyReferralCodeUpdated());
    this.keyboardProvider.setVueProvider(this);
    this.nativeProvider.liquidGlassBar.setVueProvider(this);
    // Set VueProvider instance in UtilsProvider to avoid circular dependency
    UtilsProvider.setVueProvider(this);

    VueBridgeState.retireStaleLandingLayer = () => {
      const LANDING_ROUTES = ['Welcome', 'LoginBiometrics'];
      if (
        this.container &&
        this.container.style.display === 'block' &&
        !this.elevateIframe &&
        LANDING_ROUTES.indexOf(this.currentVueRoute) >= 0
      ) {
        LoggerProvider.info(this, `Retiring stale landing iframe (${this.currentVueRoute}) before native setRoot`);
        this.hideVueBridgeIframe(TransitionType.DESTRUCT);
      }
    };

    this.events.subscribe(EVENTS_NAME.ACCERTIFY_ID_UPDATED, (transactionId: string) => this.notifyAccertifyDeviceIdUpdated(transactionId));
  }

  // Init session: real session when logged in (web reads `authToken.jwtToken`), anonymous session otherwise.
  getInitSession() {
    if (!HttpProvider.hasAuthCustomerId()) {
      return HttpProvider.sessionResponse;
    }
    return { ...this.getSession(), authToken: { jwtToken: HttpProvider.authToken } };
  }
  
  private getReferralData() {
    return {
      referralCode: this.deeplinkProvider.deeplinkPromoCode,
      referralId: this.deeplinkProvider.referralRevision,
      referralCountry: this.deeplinkProvider.deeplinkCountryCode,
    };
  }

  notifyReferralCodeUpdated(): void {
    if (this.isConnected) {
      this.postMessage(BridgeMessageFromType.ReferralCodeUpdated, { data: this.getReferralData() });
    }
  }

  sendHardwareBack(): void {
    this.postMessage(BridgeMessageFromType.HardwareBack, {});
  }

  getVueBridgeUrl(): string {
    let url: string;

    if (!this.platform.is('cordova') && APP_CONFIG.vueBridgeLocalUrl) {
      // Local Vue dev server for development
      url = `${APP_CONFIG.vueBridgeLocalUrl}/#/bridge`;
    } else {
      url = `${window.location.origin}/assets/vue-app/index.html#/bridge`;
    }

    return url;
  }

  async init(forceInit: boolean = false) {
    this.disabled = UtilsProvider.getFeatureFlagDataUnsafe(FeatureFlagTypes.Rmt5VueBridgeDisabled);
    // The 3DS Songbird bridge scope keeps the transport available even when
    // the general rmt5 bridge flag disables the UI bridge.
    if (this.disabled && !this.songbird3dsBridgeEnabled) {
      LoggerProvider.info(this, 'Vue bridge is disabled !');
      return;
    }

    if (this.initialized && !forceInit) {
      LoggerProvider.info(this, 'Vue bridge already initialized');
      return;
    }

    LoggerProvider.info(this, 'Start Vue initialization');
    
    // Set the URL when initializing (platform is ready by then)
    this.connectUrl = this.getVueBridgeUrl();
    
    this.container = document.getElementById('vue-container');
    if (!this.container) {
      LoggerProvider.error(this, LoggerEvent.Vue, 'Vue container not found in DOM');
      return;
    }

    this.iframe = this.container.getElementsByTagName('iframe')[0];
    if (!this.iframe) {
      LoggerProvider.error(this, LoggerEvent.Vue, 'Vue iframe not found in container');
      return;
    }

    const appRoot = document.querySelector('ion-app');
    if (appRoot && this.container.parentElement !== appRoot) {
      appRoot.appendChild(this.container);
    }

    // Ensure container starts completely hidden
    VueBridgeContainerHelpers.setContainerHidden(this.container);

    try {
      this.version = await this.appVersionProvider.getVersionNumber();
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Failed to resolve app version for bridge');
    }

    // Mark as initialized
    this.initialized = true;
  }

  async preConnectVueBridgeInBackground() {
    LoggerProvider.info(this, 'Pre-connecting Vue bridge in background');
    if (this.disabled) {
      LoggerProvider.info(this, 'Vue bridge is disabled, skipping pre-connect !');
      return;
    }
    
    // Ensure initialization is complete
    if (!this.initialized || !this.iframe) {
      LoggerProvider.info(this, 'Vue bridge not fully initialized yet, skipping pre-connect');
      return;
    }
    if (this.readyPromise)  {
      LoggerProvider.info(this, 'Pre-connect Vue bridge already in progress');
      return;      
    }
    
    try {
      await this.waitForAppDataReady(this.resourcesReadyTimeout);
    } catch (e) {
      LoggerProvider.error(this, LoggerEvent.Vue, 'Error while waiting for app data readiness', e);
    }

    if (!this.isConnected && !this.readyPromise) {
      await this.setupVueBridgeConnection();
    }
  }

  private delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private async waitForAppDataReady(maxWaitMs: number) {
    const waits: Promise<any>[] = [];
    const resourcesInit = this.resourceProvider.ensureInitializedResources();
    if (resourcesInit !== null && resourcesInit !== undefined) waits.push(resourcesInit);
    const featuresPromise = this.utilsProvider.getFeatures().toPromise().catch(() => null);
    waits.push(featuresPromise);
    if (waits.length === 0) return;
    await Promise.race([
      Promise.all(waits),
      this.delay(maxWaitMs)
    ]);
  }

  async setupVueBridgeConnection() {
    this.connectionAttempts = 0;
    this.isConnected = false;

    this.readyPromise = new Promise((resolve) => {
      this.readyResolve = resolve;
    });

    // Setup message handler only once
    if (!this.messageHandler) {
      this.messageHandler = async (event) => {
        await this.handleMessageEvent(event);
      };
      window.addEventListener('message', this.messageHandler, false);
    }

    // Try to connect
    await this.attemptVueBridgeConnectionWithRetry();
  }

  async attemptVueBridgeConnectionWithRetry() {
    this.connectionAttempts++;
    LoggerProvider.info(this, `Vue bridge connection attempt ${this.connectionAttempts}/${this.maxConnectionAttempts}`);

    // Ensure iframe exists before attempting connection
    if (!this.iframe) {
      LoggerProvider.error(this, LoggerEvent.Vue, 'Cannot attempt connection - iframe not found');
      return;
    }

    // Clear any existing timeout
    if (this.connectionTimeoutId) {
      clearTimeout(this.connectionTimeoutId);
    }

    // Ensure iframe is hidden before attempting connection
    VueBridgeContainerHelpers.setContainerHidden(this.container);
    
    // Set iframe source
    // For the bundled Vue app, we need to append the parent origin as a query parameter
    const parentOrigin = window.location.origin;
    const separator = this.connectUrl.includes('?') ? '&' : '?';
    const url = `${this.connectUrl}${separator}parent=${parentOrigin}`;
    
    this.iframe.src = url;
    LoggerProvider.info(this, 'Setting iframe src:', url);

    // Set a timeout for this connection attempt
    this.connectionTimeoutId = setTimeout(async () => {
      if (!this.isConnected && this.connectionAttempts < this.maxConnectionAttempts) {
        LoggerProvider.info(this, 'Vue bridge connection timeout, retrying...');
        
        // Reset iframe to force reload
        this.iframe.src = 'about:blank';
        
        // Wait a bit before retrying
        await new Promise(resolve => setTimeout(resolve, this.retryDelay));
        
        // Try again
        await this.attemptVueBridgeConnectionWithRetry();
      } else if (!this.isConnected) {
        LoggerProvider.error(this, LoggerEvent.Vue, 'Failed to connect to Vue bridge after all attempts');
        
        // Hide the iframe to prevent showing error page
        VueBridgeContainerHelpers.setContainerHidden(this.container);
        
        // Clear the iframe source to stop loading
        if (this.iframe) {
          this.iframe.src = 'about:blank';
        }
        
        // Mark navigation as disabled
        this.navigateDisabled = true;
        
        if (this.readyResolve) {
          this.readyResolve(false);
          this.readyResolve = null;
        }
      }
    }, this.connectionTimeout);
  }

  async handleMessageEvent(event) {
    // Enhanced origin validation
    const eventOrigin = event.origin || '';
    const expectedOrigins = this.getExpectedOrigins();
    
    LoggerProvider.info(this, 'Received message event:', {
      origin: eventOrigin,
      expectedOrigins: expectedOrigins,
      data: event.data ? JSON.stringify(event.data).substring(0, 100) : 'no data'
    });
    
    // Check if origin is valid
    const isValidOrigin = expectedOrigins.some(origin => eventOrigin === origin);

    // filter out Cordova/Zone.js noise
    if (typeof event.data === "string" && event.data.startsWith("setImmediate$")) {
      return; // ignore polyfill messages
    }
    
    if (!isValidOrigin) {
      LoggerProvider.info(this, 'Ignoring message from unexpected origin:', eventOrigin);
      return;
    }

    if (event.source && this.iframe && this.iframe.contentWindow && event.source !== this.iframe.contentWindow) {
      return;
    }

    // Store the valid origin for future messages
    if (!this.origin) {
      this.origin = eventOrigin;
    }

    await this.handleMessage(event.data, eventOrigin);
  }

  getExpectedOrigins(): string[] {
    const origins = [];
    
    // The Vue app will post messages from the same origin as the parent
    // since it's loaded from the same domain
    const currentOrigin = window.location.origin;
    origins.push(currentOrigin);
    
    // For development environment
    if (!this.platform.is('cordova')) {
      origins.push('http://localhost:8100');
      origins.push('http://127.0.0.1:8100');
      if (APP_CONFIG.vueBridgeLocalUrl) {
        const localUrl = new URL(APP_CONFIG.vueBridgeLocalUrl);
        origins.push(`${localUrl.protocol}//localhost:${localUrl.port}`);
        origins.push(`${localUrl.protocol}//127.0.0.1:${localUrl.port}`);
      }
    } else {
      // For mobile platforms, the origin should match the parent
      // Android uses https://localhost
      // iOS uses ionic://localhost
      if (currentOrigin === 'https://localhost') {
        origins.push('https://localhost');
      } else if (currentOrigin === 'ionic://localhost') {
        origins.push('ionic://localhost');
      } else if (currentOrigin.startsWith('file://')) {
        origins.push(currentOrigin);
      }
    }
    return origins;
  }

  getSession() {
    return {
      authToken: HttpProvider.authToken,
      refreshToken: HttpProvider.refreshToken,
      country: HttpProvider.authCountry,
      countryId: APP_CONFIG.region.regionId,
      deviceToken: HttpProvider.deviceToken,
      username: this.httpProvider.getAuthEmail(),
      resources: this.resourceProvider.resources || {},
      profile: HttpProvider.user,
      locale: HttpProvider.currentLanguage,
    };
  }

  getLandingPageAbTest(): { enabled: boolean; variant: string | null } {
    const raw = UtilsProvider.getFeatureFlagDataUnsafe(FeatureFlagTypes.Rmt5VueBridgeLandingPageAbTest);
    const enabled = typeof raw === 'string' && LANDING_PAGE_AB_TEST_VARIANTS.indexOf(raw) >= 0;
    return { enabled, variant: enabled ? raw : null };
  }

  getRmt5Flags() {
    return {
      landing: !!UtilsProvider.getFeatureFlagDataUnsafe(FeatureFlagTypes.Rmt5VueBridgeLanding),
      signup: !!UtilsProvider.getFeatureFlagDataUnsafe(FeatureFlagTypes.Rmt5VueBridgeSignup),
      login: !!UtilsProvider.getFeatureFlagDataUnsafe(FeatureFlagTypes.Rmt5VueBridgeLogin),
      home: !!UtilsProvider.getFeatureFlagDataUnsafe(FeatureFlagTypes.Rmt5VueBridgeHome),
      settings: !!UtilsProvider.getFeatureFlagDataUnsafe(FeatureFlagTypes.Rmt5VueBridgeSettings),
      sendMoney: !!UtilsProvider.getFeatureFlagDataUnsafe(FeatureFlagTypes.Rmt5VueBridgeSendMoney),
      usl: !!UtilsProvider.getFeatureFlagDataUnsafe(FeatureFlagTypes.Rmt5VueBridgeUsl),
      mobileTopUp: !!UtilsProvider.getFeatureFlagDataUnsafe(FeatureFlagTypes.Rmt5VueBridgeMobileTopUp),
      landingPageAbTest: this.getLandingPageAbTest(),
    };
  }

  // Registration only - RMT5 has no login entry into onboarding, so no 'login' arm.
  isRmt5OnboardingEnabled(): boolean {
    return !!UtilsProvider.getFeatureFlagDataUnsafe(FeatureFlagTypes.Rmt5VueBridgeSignup);
  }

  notifyAccertifyDeviceIdUpdated(accertifyDeviceId: string) {
    if (!this.isConnected || !accertifyDeviceId) {
      return;
    }
    this.postMessage(BridgeMessageFromType.AccertifyDeviceIdUpdated, { data: { accertifyDeviceId } });
  }

  isAnyWelcomeArmEnabled(): boolean {
    return (
      this.getLandingPageAbTest().enabled ||
      !!UtilsProvider.getFeatureFlagDataUnsafe(FeatureFlagTypes.Rmt5VueBridgeLanding)
    );
  }

  private async persistWelcomeCountries(countryFrom?: string, countryTo?: string) {
    try {
      if (countryFrom) {
        await this.authProvider.setUserCountry(countryFrom, true, true, true, false, false);
        await this.storageProvider.set(StorageType.WelcomePendingCountryFrom, countryFrom);
      }
      if (countryTo) {
        await this.storageProvider.set(StorageType.DefaultCountryTo, countryTo);
        await this.storageProvider.set(StorageType.WelcomePendingCountryTo, countryTo);
      }
      if (countryFrom || countryTo) {
        await this.utilsProvider.refreshCountryFeatures();
      }
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Failed to persist welcome countries');
    }
  }

  async clearWelcomePendingCountries() {
    try {
      await this.storageProvider.remove(StorageType.WelcomePendingCountryFrom);
      await this.storageProvider.remove(StorageType.WelcomePendingCountryTo);
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Failed to clear welcome pending countries');
    }
  }

  // Reads the pending Welcome corridor country and clears both pending keys,
  // so a later manual registration is not wrongly pre-filled.
  async consumePendingWelcomeCountry(): Promise<string> {
    const pendingFrom = await this.storageProvider.get(StorageType.WelcomePendingCountryFrom);
    await this.clearWelcomePendingCountries();
    return pendingFrom;
  }

  // Vue 'Registration' back-navigation → native registration; a valid pending
  // Welcome corridor skips the native country-selection step.
  private async startNativeRegistration() {
    let pendingFrom = null;
    try {
      pendingFrom = await this.consumePendingWelcomeCountry();
      if (pendingFrom) {
        const userCountries = await this.authProvider.getUserCountries() || [];
        if (userCountries.indexOf(pendingFrom) < 0) {
          LoggerProvider.info(this, `Pending welcome country ${pendingFrom} is not a selectable registration country - falling back to country selection`);
          pendingFrom = null;
        }
      }
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Failed to read pending welcome country');
      pendingFrom = null;
    }

    if (pendingFrom) {
      try {
        this.loadingProvider.showDelayed();
        await this.startRegistrationWithCountry(pendingFrom);
        return;
      } catch (ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Failed to continue registration with pending welcome country');
      } finally {
        this.loadingProvider.hide();
      }
    }

    this.routeProvider.openModal(this.routeProvider.pages.RlUserCountryPage, {
      analyticsLocation: SegmentLocationNames.Registration,
      onChangeCountry: async (country) => this.startRegistrationWithCountry(country)
    });
  }

  private async startRegistrationWithCountry(country: string) {
    this.registerProvider.setCurrentRegistration({});
    await this.utilsProvider.continueRegistrationPage({ selectedCountry: country }, true);
  }

  postMessage(type: BridgeMessageFromType, message, refMessage: any = null) {
    if (!this.isConnected || !this.origin || !this.iframe || !this.iframe.contentWindow || !this.isBridgeTransportAllowed()) {
      LoggerProvider.info(this, 'Cannot post message - bridge not connected', {
        isConnected: this.isConnected,
        hasOrigin: !!this.origin,
        hasIframe: !!this.iframe,
        hasContentWindow: !!(this.iframe && this.iframe.contentWindow)
      });
      return;
    }

    LoggerProvider.info(this, `Post message to Vue ${type}`, message);
    message.type = type;
    if (refMessage) {
      message.uid = refMessage.uid;
    }
    
    try {
      const messageStr = JSON.stringify(message);
      LoggerProvider.info(this, `Sending to origin ${this.origin}:`, messageStr.substring(0, 200));
      this.iframe.contentWindow.postMessage(messageStr, this.origin);
    } catch (e) {
      LoggerProvider.error(this, LoggerEvent.Vue, 'Failed to post message:', e);
    }
  }

  /**
   * oneApp -> Vue: ask the Vue app (secure site) to run the CyberSource + Songbird
   * (Cardinal) 3DSV2 card authorization and return the payer-authentication result.
   * Ensures the bridge is connected, shows the iframe so the challenge overlay is
   * visible, and resolves with the JWT / transaction ids (or a `cancelled` result).
   */
  /**
   * Ensure the bridge is connected, self-healing stale connection state.
   * A failed earlier attempt leaves `readyPromise` behind resolved-false (it is
   * only nulled on success or explicit disconnect), so it must not be trusted:
   * when not connected, give any in-flight attempt a bounded wait, then force a
   * fresh reconnect() instead of throwing on leftovers.
   */
  async ensureBridgeConnected(timeoutMs = 15000): Promise<boolean> {
    // Generic transport-level connect: no scope policy here. Callers that are
    // allowed to bypass the rmt5 flag enable their scope first (see
    // Songbird3dsBridgeProvider.ensureAvailable / enableSongbird3dsScope).
    if (!this.isBridgeTransportAllowed()) {
      LoggerProvider.info(this, 'Vue bridge transport is disabled - not connecting');
      return false;
    }

    // A disabled (or too-early) init() bails before wiring the container and
    // iframe — without them no connection attempt can ever succeed.
    if (!this.initialized || !this.iframe) {
      await this.init(true);
      if (!this.initialized || !this.iframe) {
        LoggerProvider.error(this, LoggerEvent.Vue, 'Vue bridge init failed - container/iframe unavailable');
        return false;
      }
    }

    if (this.isConnected) {
      return true;
    }

    // Give any in-flight connection attempt a chance to finish
    await this.waitForBridgeReady(timeoutMs);
    if (this.isConnected) {
      return true;
    }

    LoggerProvider.info(this, 'Vue bridge not connected - forcing a fresh reconnect', {
      hadReadyPromise: !!this.readyPromise,
      navigateDisabled: this.navigateDisabled,
    });
    await this.reconnect();
    await this.waitForBridgeReady(timeoutMs);
    if (!this.isConnected) {
      LoggerProvider.error(this, LoggerEvent.Vue, 'Vue bridge could not be connected', {
        hasReadyPromise: !!this.readyPromise,
        navigateDisabled: this.navigateDisabled,
        disabled: this.disabled,
        songbird3dsBridgeEnabled: this.songbird3dsBridgeEnabled,
      });
    }
    return this.isConnected;
  }

  private waitForBridgeReady(timeoutMs: number): Promise<void> {
    if (!this.readyPromise) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      let settled = false;
      const finish = () => {
        if (!settled) {
          settled = true;
          resolve();
        }
      };
      const timer = window.setTimeout(finish, timeoutMs);
      Promise.resolve(this.readyPromise).then(
        () => { window.clearTimeout(timer); finish(); },
        () => { window.clearTimeout(timer); finish(); }
      );
    });
  }

  prepareLogin(username?: string) {
    const message = {
      locale: HttpProvider.currentLanguage,
      session: this.getSession(),
      username,
    };
    this.postMessage(BridgeMessageFromType.PrepareLogin, message);
  }

  /**
   * Navigates to a specific route in the Vue bridge app.
   * @param routeName - The name of the route to navigate to in the Vue app
   * @param backPage - The page to return to when back is pressed (optional)
   * @param data - Additional data to pass to the Vue route (optional)
   * @param fallbackCallback - Function to call if Vue bridge fails (optional)
   * @param showLoading - Whether to show loading spinner while connecting (optional)
   * @returns Promise that resolves when navigation is complete
   */
  async navigate(routeName: string, backPage = '', data: VueNavigateData = {}, fallbackCallback: any = null, showLoading: boolean = false){
    LoggerProvider.info(this, `Navigating to Vue route: ${routeName}`);
    VueBridgeState.setUserInsideVueBridge(true);
    const navigateStartTime = new Date();
    this.transitionPromise = new Promise((resolve) => {
      this.transitionResolve = resolve;
    });

    if (!this.navigateDisabled) {
      // If already connected, navigation will be instant!
      if (this.isConnected) {
        LoggerProvider.info(this, 'Vue bridge already connected, instant navigation');
      } else {
        // Show loading immediately if bridge not ready
        if (showLoading) {
          this.loadingProvider.show();
        }
        
        // Setup connection if not already connected or connecting
        if (!this.readyPromise) {
          LoggerProvider.info(this, 'Vue bridge not connected, setting up connection');
          await this.setupVueBridgeConnection();
        } else {
          LoggerProvider.info(this, 'Vue bridge connection in progress, waiting...');
        }
        
        let waitForReadyResolve: any = null;
        const waitForReady = new Promise(async(resolve) => {
          waitForReadyResolve = resolve;
          await this.readyPromise;
          if (waitForReadyResolve) {
            waitForReadyResolve(true);
            waitForReadyResolve = null;
          }
        });

        let timeout: any = window.setTimeout(() => {
          const duration =  new Date().getTime() - navigateStartTime.getTime();
          LoggerProvider.error(this, LoggerEvent.Vue, `Vue navigation ready timeout for ${routeName} (${duration}ms)`);
          if (showLoading)
            this.loadingProvider.hide();
          timeout = null;
          this.navigateDisabled = true;
          waitForReadyResolve(false);
        }, 10000);

        if (showLoading)
          this.loadingProvider.showDelayed();
        await waitForReady;
        if (timeout)
          window.clearTimeout(timeout);
      }
    }

    if (this.navigateDisabled || !this.isConnected) {
      LoggerProvider.info(this, `Vue navigation is disabled or not connected for ${routeName}`);
      if (showLoading)
        this.loadingProvider.hide();
      this.transitionResolve(false);
      if (fallbackCallback) {
        LoggerProvider.info(this, `Using fallback for ${routeName} - Vue bridge not available`);
        // Use immediate fallback without waiting
        setTimeout(() => fallbackCallback(), 0);
      }
      return null;
    }

    this.currentVueRoute = routeName;
    this.navigatePromise = new Promise((resolve) => {
      this.navigateResolve = resolve;

      let message: any = {
        routeName,
        backPage,
        locale: HttpProvider.currentLanguage,
        countryId: APP_CONFIG.region.regionId,
        ...data
      };
      const shouldAttachSession = this.httpProvider.getAuthUserId() > 0 && routeName !== 'Login';
      if (shouldAttachSession) {
        message = {
          ...message,
          session: this.getSession(),
        };
      }
      this.notifyReferralCodeUpdated();
      this.postMessage(BridgeMessageFromType.Push, message);
    });

    let timeout: any = window.setTimeout(() => {
      const duration =  new Date().getTime() - navigateStartTime.getTime();
      LoggerProvider.error(this, LoggerEvent.Vue, `Vue navigation timeout for ${routeName} (${duration}ms)`);
      timeout = null;
      this.transitionResolve(false);
    }, 10000);

    const result = await this.transitionPromise;

    const duration =  new Date().getTime() - navigateStartTime.getTime();
    if (showLoading)
      this.loadingProvider.hide();
    if (timeout)
      window.clearTimeout(timeout);
    if (!result) {
      this.navigateDisabled = true;
      if (fallbackCallback) {
        LoggerProvider.error(this, LoggerEvent.Vue, `Fallback for Vue navigation for ${routeName} (${duration}ms)`);
        fallbackCallback();
      } else {
        LoggerProvider.error(this, LoggerEvent.Vue, `Vue navigation failed for ${routeName} (${duration}ms)`);
      }
      return null;
    }
    LoggerProvider.info(this, `Vue navigation finished for ${routeName} (${duration}ms)`);

    return this.navigatePromise;
  }

  /**
   * Navigates to Vue profile completion flow with proper callback handling.
   * Consolidates the Vue bridge setup logic for profile completion scenarios.
   * @param onComplete - Callback to execute when profile completion succeeds
   * @param onFallback - Callback to execute if Vue navigation fails (falls back to in-app flow)
   * @param showLoading - Whether to show loading indicator during navigation
   */
  async navigateToProfileCompletion(
    onComplete: () => Promise<void> | void,
    onFallback: () => void,
    showLoading: boolean = true
  ): Promise<void> {
    VueBridgeState.setUserInsideVueBridge(true);
    VueBridgeState.onCompleteCallback = async () => {
      await onComplete();
    };

    this.navigate('CompleteProfileInfo', '', {}, () => {
      // Fallback to in-app flow if Vue navigation fails
      LoggerProvider.info(this, 'Vue navigation failed for profile completion, falling back to in-app flow');
      VueBridgeState.onCompleteCallback = null;
      VueBridgeState.setUserInsideVueBridge(false);
      onFallback();
    }, showLoading);
  }

  async navigateToBankDeposit(
    entry: BridgeBankDepositEntry,
    onComplete: (data: BridgeBankDepositCompleteData) => Promise<void> | void,
    onFallback: () => void,
    showLoading: boolean = true
  ): Promise<void> {
    VueBridgeState.setUserInsideVueBridge(true);
    VueBridgeState.onCompleteCallback = null;
    this.elevateIframe = true;
    this.restoreVueRouteAfterBankDeposit =
      this.container && this.container.style.display === 'block' && this.currentVueRoute
        ? this.currentVueRoute
        : null;
    this.bankDepositCompleteCallback = async (data: BridgeBankDepositCompleteData) => {
      await onComplete(data);
    };

    this.navigate(BridgeBankDepositRoute.TopBanks, '', { bankDeposit: entry }, () => {
      LoggerProvider.info(this, 'Vue navigation failed for bank deposit, falling back to in-app flow');
      this.bankDepositCompleteCallback = null;
      this.elevateIframe = false;
      this.restoreVueRouteAfterBankDeposit = null;
      VueBridgeState.setUserInsideVueBridge(false);
      onFallback();
    }, showLoading);
  }

  /** True when a post-login redirect left a Vue landing page covering the app. */
  isPostLoginVueHome(): boolean {
    return VueProvider.POST_LOGIN_VUE_HOMES.indexOf(this.currentVueRoute) !== -1;
  }

  isRmt5HomeActiveBaseLayer(): boolean {
    return !!(this.isConnected && this.currentVueRoute === 'RMT5Home' &&
        this.container && this.container.style.display === 'block');
  }

  async waitForTransition() {
    const result = await this.transitionPromise;
    return result;
  }

  /** Resolves once the slide-in fully covers the app. */
  async waitForBridgeCover(): Promise<void> {
    await this.bridgeCoverPromise;
  }

  async navigateToHomeOrFallback(fallbackPage: any): Promise<boolean> {
    const rmt5VueBridgeHome = UtilsProvider.getFeatureFlagDataUnsafe(FeatureFlagTypes.Rmt5VueBridgeHome);
    if (rmt5VueBridgeHome) {
      this.navigate('RMT5Home', '', { replace: true }, null, true);
      const success = await this.waitForTransition();
      if (success) {
        LoggerProvider.info(this, 'Redirect to Vue home page successful');
        if (fallbackPage) {
          await this.waitForBridgeCover();
          this.routeProvider.setRoot(fallbackPage, {}, false);
        }
        return true;
      }
      LoggerProvider.info(this, 'Redirect to Vue home page failed, falling back to native');
    }

    this.hideVueBridgeIframe(TransitionType.DESTRUCT);
    if (fallbackPage) {
      setTimeout(() => {
        this.routeProvider.setRoot(fallbackPage);
      }, 500);
    }
    return false;
  }

  /**
   * Shows the Vue bridge iframe container on screen.
   * This makes the Vue app visible to the user for registration/navigation.
   * Ensures the bridge is connected before showing.
   */
  async showVueBridgeIframe(animate: boolean = true) {
    this.nativeProvider.liquidGlassBar.setIframeHidden(false);
    try {
      LoggerProvider.info(this, 'showVueBridgeIframe() method called', { animate });

      // Wait for ready promise only if it exists and hasn't resolved yet
      if (this.readyPromise) {
        LoggerProvider.info(this, 'Vue Bridge: Waiting for readyPromise in show()');
        await this.readyPromise;
      }

      // Only show if connected
      if (this.container && this.isConnected) {
        this.vueVisibilitySeq++;
        this.container.style.zIndex = this.elevateIframe ? '100000' : '1';
        VueBridgeState.setUserInsideVueBridge(true);

        // only slide in when not on screen; re-sliding flashes the native stack
        if (animate && !VueBridgeContainerHelpers.isContainerVisible(this.container)) {
          VueBridgeContainerHelpers.prepareSlideIn(this.container);

          // Force browser to apply the initial state before transitioning
          VueBridgeContainerHelpers.forceReflow(this.container);

          // resolve once the container covers the app
          const covered = VueBridgeContainerHelpers.waitForSlideIn(this.container);
          requestAnimationFrame(() => {
            VueBridgeContainerHelpers.triggerSlideIn(this.container);
          });
          await covered;
        } else {
          // Show instantly without animation
          this.container.style.display = 'block';
          this.container.style.opacity = '1';
          this.container.style.transform = 'translateX(0)';
        }

        // Ensure iframe is also visible
        if (this.iframe) {
          this.iframe.style.display = 'block';
          this.iframe.style.visibility = 'visible';
        }
      } else {
        LoggerProvider.info(this, 'Vue bridge show() called but not connected or container missing', {
          hasContainer: !!this.container,
          isConnected: this.isConnected
        });
      }
    } catch (e) {
      LoggerProvider.error(this, LoggerEvent.Vue, 'Error in show() method:', e);
    }
  }

  /**
   * Hides the Vue bridge iframe container from view.
   * This removes the Vue app from the user's screen and notifies
   * the Vue app that it's being hidden.
   * @param transitionType - Type of transition to use (slide-animation or destruct)
   * @returns Promise that resolves when animation is complete
   */
  hideVueBridgeIframe(transitionType: TransitionType = TransitionType.SLIDE_ANIMATION): Promise<void> {
    this.nativeProvider.liquidGlassBar.setIframeHidden(true);
    this.bridgeVisible$.next(false);
    LoggerProvider.info(this, `Hide Vue with ${transitionType} transition`);
    VueBridgeState.setUserInsideVueBridge(false);

    const seq = ++this.vueVisibilitySeq;
    if (this.pendingReloginResolve) {
      // the login left the screen without finishing; nothing would ever settle the relogin
      LoggerProvider.info(this, 'Iframe hidden during relogin - abandoning relogin');
      this.cancelVueRelogin(false);
    }
    this.pendingPostLoginCallback = null;
    this.elevateIframe = false;
    return new Promise((resolve) => {
      if (this.container) {
        // Apply the appropriate transition based on type
        if (transitionType === TransitionType.DESTRUCT) {
          // Destruct: just fade out without movement
          VueBridgeContainerHelpers.triggerDestruct(this.container);
        } else {
          VueBridgeContainerHelpers.triggerSlideOut(this.container);
        }

        // After animation completes, hide the container
        setTimeout(() => {
          if (seq === this.vueVisibilitySeq) {
            VueBridgeContainerHelpers.setContainerHidden(this.container);
            this.elevateIframe = false;
            this.container.style.zIndex = '1';
          }
          resolve();
        }, 400);
      } else {
        resolve();
      }
      // Notify Vue app that it's being hidden
      this.postMessage(BridgeMessageFromType.Hiding, {});
    });
  }

  async waitForPostLoginPageReady(timeoutMs: number = 20000): Promise<boolean> {
    try {
      const start = new Date().getTime();
      const pollDelayMs = 100;
      while (true) {
        // guard against a stale transition promise that never settles
        const remainingMs = Math.max(timeoutMs - (new Date().getTime() - start), 0);
        const currentPageName = await Promise.race([
          this.routeProvider.getCurrentPageName(),
          new Promise<null>(resolve => setTimeout(() => resolve(null), remainingMs)),
        ]);
        if (currentPageName && currentPageName !== 'PreLoginStoriesPage') {
          LoggerProvider.info(this, `Post-login page ready: ${currentPageName}`);
          return true;
        }
        if (new Date().getTime() - start >= timeoutMs) {
          LoggerProvider.info(this, 'Timeout waiting for post-login page readiness');
          return false;
        }
        await new Promise(resolve => setTimeout(resolve, pollDelayMs));
      }
    } catch (e) {
      LoggerProvider.error(this, LoggerEvent.Vue, 'Error while waiting for post-login page readiness', e);
      return false;
    }
  }

  async waitForRecipientFlowReady(timeoutMs: number = 15000): Promise<boolean> {
    try {
      const start = new Date().getTime();
      const pollDelayMs = 100;
      while (true) {
        await this.routeProvider.waitForTransitions();
        const currentPageName = await this.routeProvider.getCurrentPageName();
        const lastPushedPage = this.routeProvider.getLastPushedPage();
        const lastPushedPageName = lastPushedPage ? lastPushedPage.page : null;
        const isRecipientPage =
          currentPageName === this.routeProvider.pages.SendToNewRecipientPage.page ||
          currentPageName === this.routeProvider.pages.SendToRecipientsPage.page;
        const isRecipientModal =
          this.routeProvider.checkPageIsModal() &&
          (lastPushedPageName === this.routeProvider.pages.SendToNewRecipientPage.page ||
           lastPushedPageName === this.routeProvider.pages.SendToRecipientsPage.page);

        if (isRecipientPage || isRecipientModal) {
          LoggerProvider.info(this, `Recipient flow ready: ${isRecipientModal ? lastPushedPageName : currentPageName}`);
          return true;
        }
        if (new Date().getTime() - start > timeoutMs) {
          LoggerProvider.info(this, 'Timeout waiting for recipient flow readiness');
          return false;
        }
        await new Promise(resolve => setTimeout(resolve, pollDelayMs));
      }
    } catch (e) {
      LoggerProvider.error(this, LoggerEvent.Vue, 'Error while waiting for recipient flow readiness', e);
      return false;
    }
  }

  async handleMessage(eventData, eventOrigin) {
      if (typeof eventData !== 'string' || !eventData.startsWith('{')) {
        return;
      }
      let message: any;
      try {
        message = JSON.parse(eventData);
      } catch (ex) {
        LoggerProvider.info(this, 'Ignoring non-JSON postMessage payload', eventData.substring(0, 80));
        return;
      }
      LoggerProvider.info(this, `Received message from Vue: ${message.type}`);

      if (!this.isConnected && message.type !== BridgeMessageToType.Ready) {
        return;
      }
      
      if (message.type === BridgeMessageToType.Ready) {
        // Connection established!
        this.isConnected = true;
        
        // Clear connection timeout
        if (this.connectionTimeoutId) {
          clearTimeout(this.connectionTimeoutId);
          this.connectionTimeoutId = null;
        }

        LoggerProvider.profileLogEntry(this.startTime, this, `Vue initialization finished on attempt ${this.connectionAttempts}`);
        
        // Get resources and features, with fallback to empty object if not available
        let resources;
        let features = null;
        try {
          // Ensure resources are initialized
          await this.resourceProvider.initializeResources();
          resources = await this.resourceProvider.getResources();
          if (!resources) {
            LoggerProvider.info(this, 'Resources not available yet, using fallback');
            resources = this.resourceProvider.resources || {};
          }
          features = await this.utilsProvider.getFeatures().toPromise().catch(() => null);
        } catch (e) {
          LoggerProvider.error(this, LoggerEvent.Vue, 'Error getting resources:', e);
          resources = this.resourceProvider.resources || {};
        }
        // Attach features to resources object; ensure safe default structure
        const defaultFeatures = { CountriesFeatures: [], CountrySettings: [], AgentFeatures: [] };
        if (!features) features = defaultFeatures;
        if (!resources) {
          resources = {} as any;
        }
        if (!(resources as any).FeaturesTask) {
          (resources as any).FeaturesTask = features;
        }

        this.origin = eventOrigin;
        if (this.readyResolve) {
          this.readyResolve(true);
          this.readyResolve = null;
          this.readyPromise = null; // Clear the promise after resolving
        }
        
        let isCountryReadonly = await this.registerProvider.isOriginalCountryAllowedOnly();
        let isProd = APP_CONFIG.env === Env.PROD;

        // reset full-bleed first: insets are read off the padding it zeroes
        this.setFullBleed(false);

        // Send init message with available data.
        const accertifyDeviceId = OrderProvider.AccertifyTransactionID ||
          (OrderProvider.AccertifyTransactionPromise ? null : await this.orderProvider.getAccertifyTransactionId());
        const initData = {
          countryId: APP_CONFIG.region.regionId,
          locale: HttpProvider.currentLanguage,
          session: this.getInitSession(),
          resources: resources || {},
          isCountryReadonly: isCountryReadonly,
          isProd: isProd,
          version: this.version,
          rmt5Flags: this.getRmt5Flags(),
          accertifyDeviceId: accertifyDeviceId || null,
          riskifiedSessionToken: LoggerProvider.globalSessionId || null,
          ...this.getReferralData(),
          // env(safe-area-inset-*) reads 0 inside the iframe
          safeAreaInsets: this.getSafeAreaInsets(),
          liquidGlassBarAvailable: await this.nativeProvider.liquidGlassBar.isBarAvailable(),
        };

        this.postMessage(BridgeMessageFromType.Init, { data: initData });
      } else if (message.type === BridgeMessageToType.ReferralCodeConsumed) {
        if (message.data && typeof message.data.referralId === 'number') {
          this.deeplinkProvider.clearDeeplinkReferral(message.data.referralId);
        }
      } else if (message.type === BridgeMessageToType.Back) {
        if (this.pendingOnboardingSignin) {
          LoggerProvider.info(this, 'Back during RMT5 onboarding - finalizing login natively so the user reaches home');
          await this.finalizeOnboarding({ ranByRmt5: false });
          return;
        }
        if (this.bankDepositCompleteCallback) {
          LoggerProvider.info(this, 'Back during bank deposit handoff — cancelling overlay');
          this.bankDepositCompleteCallback = null;
          await this.endBankDepositOverlay();
          if (this.navigatePromise) {
            this.navigateResolve(message.data);
            this.navigatePromise = null;
          }
          return;
        }

        // Determine which transition to use based on navigation type
        let transitionType = TransitionType.SLIDE_ANIMATION; // Default
        let shouldNavigate = false;
        let navigationTarget = null;
        let navParams = {};

        // Check if the Vue app is requesting specific navigation
        if (message.data && message.data.isBackPage) {
          LoggerProvider.info(this, `Vue requested navigation to: ${message.data.isBackPage} (from ${this.currentVueRoute})`);

          // Welcome is the root landing page — ignore back from it
          if (message.data.isBackPage === 'Welcome') {
            LoggerProvider.info(this, 'Ignoring back from Welcome (root page)');
            return;
          }

          if (message.data.isBackPage === 'Login' && this.pendingReloginResolve) {
            LoggerProvider.info(this, 'Back from Login during relogin - abandoning relogin');
            this.cancelVueRelogin(false);
            if (this.navigatePromise) {
              this.navigateResolve(message.data);
              this.navigatePromise = null;
            }
            return;
          }

          if (message.data.isBackPage === 'Login' || message.data.isBackPage === 'ForgotPassword') {
            transitionType = TransitionType.DESTRUCT;
            shouldNavigate = true;
            const loginValue = message.data.email || message.data.phoneNumber;
            // Determine navigation target and prepare params
            if (message.data.isBackPage === 'Login') {
              navigationTarget = this.routeProvider.pages.SigninPage;
              if (loginValue) {
                navParams = { username: loginValue };
              }
            } else if (message.data.isBackPage === 'ForgotPassword') {
              navigationTarget = this.routeProvider.pages.SignInForgotResetPassPage;
              if (loginValue) {
                navParams = { loginName: loginValue };
              }
            }
          } else if (message.data.isBackPage === 'Registration') {
            await this.hideVueBridgeIframe(TransitionType.DESTRUCT);
            await this.startNativeRegistration();
            if (this.navigatePromise) {
              this.navigateResolve(message.data);
              this.navigatePromise = null;
            }
            return;
          } else if (message.data.isBackPage === 'Help') {
            transitionType = TransitionType.DESTRUCT;
            shouldNavigate = true;
            navigationTarget = this.routeProvider.pages.HelpPage;
            navParams = { modal: true };
          } else if (message.data.isBackPage === 'CompleteProfileInfo') {
            LoggerProvider.info(this, 'Back from CompleteProfileInfo — restoring CheckPricesPage');
            VueBridgeState.onCompleteCallback = null;
            const profileBackCallback = VueBridgeState.onProfileBackCallback;
            VueBridgeState.onProfileBackCallback = null;
            await this.hideVueBridgeIframe(TransitionType.SLIDE_ANIMATION);
            if (this.navigatePromise) {
              this.navigateResolve(message.data);
              this.navigatePromise = null;
            }
            if (profileBackCallback) {
              try {
                profileBackCallback();
              } catch (ex) {
                LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Error invoking onProfileBackCallback');
              }
            }
            return;
          } else if (message.data.isBackPage === 'CheckRates') {
            // Open CheckRates on top of the Vue iframe — don't hide iframe
            const data = message.data as BridgeBackData;
            const recipient = data.recipient;
            if (this.httpProvider.getAuthUserId() > 0) {
              LoggerProvider.info(this, `CheckRates: logged-in user — opening send money flow (sendFromRecipient=${!!recipient})`);
              if (recipient) {
                this.orderProvider.setCurrentOrder(null);
                this.routeProvider.openModal(this.routeProvider.pages.CheckPricesPage, { recipient, sendFromRecipient: true });
              } else {
                this.homescreenProvider.setHomeScreenLocation(SegmentLocationNames.SendMoney);
                this.sendFlowProvider.sendMoneyBtn();
              }
              return;
            }
            LoggerProvider.info(this, `Opening CheckRates on top of Vue iframe (sendFromRecipient=${!!recipient})`);
            try {
              const country = await this.storageProvider.get(StorageType.UserCountry);
              if (country) {
                const defaultCountry = await this.storageProvider.get(StorageType.DefaultCountryTo);
                if (defaultCountry) {
                  const params: any = { defaultCountry };
                  if (recipient) {
                    params.recipient = recipient;
                    params.sendFromRecipient = true;
                  }
                  this.routeProvider.openModal(this.routeProvider.pages.CheckPricesPage, params);
                } else {
                  if (recipient) {
                    LoggerProvider.info(this, 'CheckRates: recipient received but no DefaultCountryTo stored — falling through to UserCountryPage');
                  }
                  this.routeProvider.openModal(this.routeProvider.pages.UserCountryPage, {
                    analyticsLocation: 'CheckPrices'
                  });
                }
              } else {
                if (recipient) {
                  LoggerProvider.info(this, 'CheckRates: recipient received but no UserCountry stored — falling through to UserCountryPage');
                }
                this.routeProvider.openModal(this.routeProvider.pages.UserCountryPage, {
                  analyticsLocation: 'CheckPrices'
                });
              }
            } catch (ex) {
              LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Failed to open CheckRates');
              this.routeProvider.openModal(this.routeProvider.pages.UserCountryPage, {
                analyticsLocation: 'CheckPrices'
              });
            }
            return;
          } else if (
            message.data.isBackPage === 'OnfidoInfoPage' ||
            message.data.isBackPage === 'SignicatVerifyPage'
          ) {
            const ekycPage = message.data.isBackPage === 'OnfidoInfoPage'
              ? this.routeProvider.pages.OnfidoInfoPage
              : this.routeProvider.pages.SignicatVerifyPage;
            const goHome = () => {
              this.loginProvider.isProfileCallNotRequired = false;
              this.routeProvider.setRoot(this.routeProvider.pages.TabsHomeActivityPage);
            };
            const ekycParams = {
              analyticsLocation: SegmentLocationNames.Settings,
              fromSettingsPage: true,
              showBack: true,
              onSuccess: goHome,
              ...(message.data || {}),
            };
            await this.hideVueBridgeIframe(TransitionType.DESTRUCT);
            this.routeProvider.setRoot(ekycPage, ekycParams);
            if (this.navigatePromise) {
              this.navigateResolve(message.data);
              this.navigatePromise = null;
            }
            return;
          }
        }

        // Hide with appropriate transition and wait for completion
        await this.hideVueBridgeIframe(transitionType);

        // After animation completes, navigate if needed
        if (shouldNavigate && navigationTarget) {
          LoggerProvider.info(this, 'Animation complete, now navigating to:', navigationTarget);
          if (message.data.isBackPage === 'Help') {
            this.routeProvider.openModal(navigationTarget, navParams);
          } else {
            await this.routeProvider.push(navigationTarget, navParams);
          }
        }
        
        if (this.navigatePromise) {
          this.navigateResolve(message.data);
          this.navigatePromise = null;
        }
      } else if (message.type === BridgeMessageToType.PushConfirm) {
        // resolve before the slide-in ends so navigate()'s timeout can't win
        this.bridgeCoverPromise = this.showVueBridgeIframe();
        if (this.transitionResolve)
          this.transitionResolve(true);
        await this.bridgeCoverPromise;
      } else if (message.type === BridgeMessageToType.Refresh) {
        await this.handleRefresh(message);
      } else if (message.type === BridgeMessageToType.Login) {
        await this.handleLogin(message);
      } else if (message.type === BridgeMessageToType.Logout) {
        await this.handleLogout();
      } else if (message.type === BridgeMessageToType.SetLanguage) {
        await this.customerProvider.setLanguage(message.data && message.data.language);
      } else if (message.type === BridgeMessageToType.DataChanged) {
        await this.handleDataChanged(message);
      } else if (message.type === BridgeMessageToType.SyncData) {
        await this.handleSyncData(message);
      } else if (message.type === BridgeMessageToType.Identify) {
        await this.handleIdentify(message);
      } else if (message.type === BridgeMessageToType.TrackEvent) {
        await this.handleTrackEvent(message);
      } else if (message.type === BridgeMessageToType.BranchEvent) {
        await this.vueBranchProvider.handleBranchEvent(message);
      } else if (message.type === BridgeMessageToType.Page) {
        await this.handlePage(message);
      } else if (message.type === BridgeMessageToType.HttpRequestGet ||
                 message.type === BridgeMessageToType.HttpRequestPost ||
                 message.type === BridgeMessageToType.HttpRequestPut ||
                 message.type === BridgeMessageToType.HttpRequestPatch ||
                 message.type === BridgeMessageToType.HttpRequestDelete) {
        await this.handleHttp(message.type, message);
      } else if (message.type === BridgeMessageToType.Native) {
        await this.handleNative(message);
      } else if (message.type === BridgeMessageToType.LocalStorage) {
        await this.handleLocalStorage(message);
      } else if (message.type === BridgeMessageToType.Signin) {
        await this.handleSignin(message);
      } else if (message.type === BridgeMessageToType.ValidateOTP) {
        await this.handleValidateOTP(message);
      } else if (message.type === BridgeMessageToType.ResendOTP) {
        await this.handleResendOTP(message);
      } else if (message.type === BridgeMessageToType.LoginComplete) {
        await this.handleLoginComplete(message);
      } else if (message.type === BridgeMessageToType.RegistrationComplete) {
        await this.handleRegistrationComplete(message);
      } else if (message.type === BridgeMessageToType.OnboardingComplete) {
        await this.handleOnboardingComplete(message);
      } else if (message.type === BridgeMessageToType.CompleteProfileComplete) {
        await this.handleCompleteProfileComplete(message);
      } else if (message.type === BridgeMessageToType.BankDepositComplete) {
        await this.handleBankDepositComplete(message);
      } else if (message.type === BridgeMessageToType.CloseIframe) {
        await this.handleCloseIframe(message);
      } else if (message.type === BridgeMessageToType.FetchResources) {
        await this.handleFetchResources(message);
      } else if (message.type === BridgeMessageToType.OpenExternalUrl) {
        await this.handleOpenExternalUrl(message);
      } else if (message.type === BridgeMessageToType.OpenNativeModal) {
        await this.handleOpenNativeModal(message);
      } else if (message.type === BridgeMessageToType.OpenEkycVerification) {
        await this.openEkycVerificationFlow(message.data);
      } else if (message.type === BridgeMessageToType.OpenSendMoney) {
        await this.openSendMoneyFlow(message.data);
      } else if (message.type === BridgeMessageToType.RecipientOpenSendMoney) {
        await this.openRecipientSendMoneyFlow(message.data);
      } else if (message.type === BridgeMessageToType.TransferToOneApp) {
        await this.handleTransferToOneApp(message);
      } else if (message.type === BridgeMessageToType.WelcomeCountrySelected) {
        const data = (message.data || {}) as BridgeWelcomeCountrySelectedData;
        await this.persistWelcomeCountries(data.countryFrom, data.countryTo);
      } else if (message.type === BridgeMessageToType.RedirectFullRegistration) {
        await this.handleRedirectFullRegistration(message);
      } else if (message.type === BridgeMessageToType.OpenDebugMenu) {
        await this.handleOpenDebugMenu();
      } else if (message.type === BridgeMessageToType.SetFullBleed) {
        this.setFullBleed(!!(message.data && message.data.fullBleed));
      } else if (message.type === BridgeMessageToType.HardwareBackUnhandled) {
        if (this.pendingReloginResolve) {
          // the login is the root page in RMT5, so a hardware back there means "give up"
          this.cancelVueRelogin(false);
        } else if (this.onHardwareBackUnhandled) {
          this.onHardwareBackUnhandled();
        }
      } else if (message.type === BridgeMessageToType.Authorize3DSFiservResult) {
        if (this.songbird3dsBridge)
          this.songbird3dsBridge.handleResult(message);
      } else if (message.type === BridgeMessageToType.Authorize3DSFiservReady) {
        if (this.songbird3dsBridge)
          this.songbird3dsBridge.handleReady(message);
      } else if (message.type === BridgeMessageToType.Authorize3DSEnrollment) {
        if (this.songbird3dsBridge)
          await this.songbird3dsBridge.handleEnrollmentRequest(message);
      } else if (message.type === BridgeMessageToType.OpenPartnerCheckout) {
        // Not awaited: the checkout stays open for minutes and replies on its own uid.
        this.handleOpenPartnerCheckout(message);
      }
      LoggerProvider.info(this, `Finished message type ${message.type}`);
  }

  // full-bleed routes draw under the status bar
  setFullBleed(fullBleed: boolean) {
    if (this.container) this.container.classList.toggle('full-bleed', fullBleed);
  }

  // padding is env(safe-area-inset-*); call after setFullBleed(false)
  getSafeAreaInsets(): { top: number; bottom: number } {
    const style = this.container ? getComputedStyle(this.container) : null;
    return {
      top: style ? parseFloat(style.paddingTop) || 0 : 0,
      bottom: style ? parseFloat(style.paddingBottom) || 0 : 0,
    };
  }

  async handleRefresh(message) {
    if (this.handlingLoginComplete) {
      LoggerProvider.info(this, 'Skipping refresh during login-complete processing');
      const response = {
        data: {
          session: this.getSession()
        }
      };
      this.postMessage(BridgeMessageFromType.RefreshFinished, response, message);
      return;
    }
    await this.httpProvider.refresh();
    const response = {
      data: {
        session: this.getSession()
      }
    };
    this.postMessage(BridgeMessageFromType.RefreshFinished, response, message);
  }

  async handleDataChanged(message) {
    const scope = message && message.data && message.data.scope;
    LoggerProvider.info(this, `DataChanged scope: ${scope}`);
    try {
      switch (scope) {
        case 'profile':
        case 'documentId':
        case 'notifications':
          await this.loginProvider.infoClient();
          break;
        case 'paymentMethods':
          await this.paymentProvider.getPaymentAccounts(true);
          break;
        case 'password':
          break;
        default:
          LoggerProvider.info(this, `DataChanged: unknown scope '${scope}', no-op`);
      }
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, `DataChanged refresh failed for scope ${scope}`);
    }
  }

  async handleLogin(message) {
      const response = message.data.response;
      this.loginProvider.setDataToken(response);
      const signin = AuthMapper.fromSigninEntity(response.Value);
      this.httpProvider.setAuthToken(signin.authToken);
      this.authProvider.login = response.Value;

      const result = await this.authProvider.initializeUser(false, false, false, true);
      if (!result) {
        RmtPopupComponent.showError('Failed to initialize automatic login - please try to login manually');
        return false;
      }
      // Older bundles don't send the identifier.
      const { identifier, identifierType } = message.data;
      if (identifier && (identifierType === 'email' || identifierType === 'phone')) {
        await this.authProvider.setLastLoginFromBridge(identifier);
      }
      const firstNameLogin = this.authProvider.getUserFirstName();
      if (firstNameLogin) {
        this.storageProvider.set(StorageType.LastLoginFirstName, firstNameLogin);
      }
      await this.authProvider.loginFirstRedirection();
      if (!this.isPostLoginVueHome()) {
        await this.hideVueBridgeIframe(TransitionType.DESTRUCT);
      }
  }

  async handleSignin(message) {
    try {
      LoggerProvider.info(this, 'Handling signin request from Vue', JSON.stringify(message.data));
      const { username, password } = message.data;

      if (!username || !password) {
        this.postMessage(BridgeMessageFromType.SigninResult, {
          data: { success: false, error: 'Username and password are required' }
        }, message);
        return;
      }

      // Store credentials for later use in handleLoginComplete
      this.pendingLoginUsername = username;
      this.pendingLoginPassword = password;
      this.pendingEmailOtp = null;

      const loginMethod = this.utilsProvider.checkAndReturnLoginMethod(username);
      const deviceToken = HttpProvider.deviceToken || await this.storageProvider.get(StorageType.DeviceToken);

      const signinPayload: any = {
        username: username,
        password: password,
        deviceId: LoggerProvider.uuid,
        deviceToken: deviceToken,
      };

      let captchaSession = null;
      try {
        captchaSession = await XeCaptchaComponent.getTokenStatic(
          this.scriptProvider,
          this.httpProvider,
          SegmentLocationNames.Login
        );
      } catch (error) {
        LoggerProvider.warning(this, LoggerEvent.Vue, 'Failed to get captcha session:', error);
      }

      try {
        const signinResult = await this.authProvider.signin(signinPayload, loginMethod, captchaSession, SegmentLocationNames.Login, null, true);

        if (!signinResult) {
          this.postMessage(BridgeMessageFromType.SigninResult, {
            data: { success: false, error: 'Signin failed' }
          }, message);
          return;
        }

        this.pendingSigninResult = signinResult;
        this.pendingSigninResult.password = password;

        if (signinResult.authenticationResult && (signinResult.authenticationResult.emailOtpRequired || signinResult.authenticationResult.requiresVerification)) {
          try {
            const otpResponse = await this.loginProvider.passSecurityQuestionsWithOtp(username, DELIVERY_TYPE.EMAIL);

            if (otpResponse && otpResponse.Value && otpResponse.Result && otpResponse.Result.RetVal === OTP_REGISTRATION_RET_VAL.OK) {
              this.pendingEmailOtp = { referenceValue: otpResponse.Value.ReferenceValue, securityCode: otpResponse.Value.SecurityCode };
              this.postMessage(BridgeMessageFromType.SigninResult, {
                data: {
                  success: true,
                  requireOtp: true,
                  otpData: {
                    deliveryType: DELIVERY_TYPE.EMAIL,
                    maskedEmail: otpResponse.Value.MaskedEmail,
                    referenceValue: otpResponse.Value.ReferenceValue,
                    securityCode: otpResponse.Value.SecurityCode,
                    otpCode: otpResponse.Value.Code,
                  }
                }
              }, message);
              return;
            }

            this.postMessage(BridgeMessageFromType.SigninResult, {
              data: {
                success: false,
                error: (otpResponse && otpResponse.Result) ? otpResponse.Result.RetMsg : 'Failed to send email OTP',
                errorCode: (otpResponse && otpResponse.Result) ? otpResponse.Result.RetVal : null,
              }
            }, message);
            return;
          } catch (otpEx) {
            LoggerProvider.exception(this, otpEx, LoggerEvent.Vue, 'Failed to send email OTP');
            this.postMessage(BridgeMessageFromType.SigninResult, {
              data: { success: false, error: 'Failed to send email OTP' }
            }, message);
            return;
          }
        }

        this.postMessage(BridgeMessageFromType.SigninResult, {
          data: {
            success: true,
            requireOtp: false,
            result: {
              token: signinResult.authToken ? signinResult.authToken.jwt : null,
              refreshToken: signinResult.authToken ? signinResult.authToken.refresh : null,
              deviceToken: deviceToken,
              requirePhoneUpdate: signinResult.authenticationResult ? signinResult.authenticationResult.requirePhoneUpdate : false,
            }
          }
        }, message);

      } catch (ex) {
        const status = ex && (ex.status || (ex.error && ex.error.status));
        const headers = ex && (ex.headers || (ex.error && ex.error.headers));

        if (status === 307) {
          LoggerProvider.info(this, 'Signin requires OTP', JSON.stringify({ status, hasHeaders: !!headers }));

          // Extract OTP data from response headers — same approach as authProvider.handleAuthErrors
          let otpObject: any = {};
          if (headers) {
            if (this.platform.is('cordova')) {
              const headerKeys = Object.keys(headers);
              headerKeys.forEach((key) => {
                const lowerKey = key.toLowerCase();
                if (lowerKey === 'waitingotptime')
                  otpObject[lowerKey] = Date.parse(headers[key]);
                else
                  otpObject[lowerKey] = headers[key];
              });
            } else {
              const headerKeys = typeof headers.keys === 'function' ? headers.keys() : Object.keys(headers);
              headerKeys.forEach((key) => {
                const lowerKey = key.toLowerCase();
                const value = typeof headers.get === 'function' ? headers.get(key) : headers[key];
                if (lowerKey === 'waitingotptime')
                  otpObject[lowerKey] = Date.parse(value);
                else
                  otpObject[lowerKey] = value;
              });
            }
          }
          const otpData = AuthMapper.fromOtpHeader(otpObject);
          LoggerProvider.info(this, 'OTP data extracted', JSON.stringify(otpData));

          this.postMessage(BridgeMessageFromType.SigninResult, {
            data: {
              success: true,
              requireOtp: true,
              otpData: {
                maskedPhone: otpData.maskedPhone,
                maskedEmail: otpData.maskedEmail,
                referenceValue: otpData.referenceValue,
                securityCode: otpData.securityCode,
                otpCode: otpData.otpCode,
                waitingOtpTime: otpData.waitingOtpTime,
                sessionKey: otpData.sessionKey,
                remainingAttempts: otpData.OTPRemainingAttempts,
              }
            }
          }, message);
        } else if (ex && ex.Result && (ex.Result.RetVal === LOGIN_ERRORS_NEW_TOKEN.VERIFY_PHONE_NUMBER || ex.Result.RetVal === LOGIN_ERRORS_NEW_TOKEN.MFA_MIGRATION_NOT_VERIFIED)) {
          LoggerProvider.info(this, 'Signin requires phone verification, delegating to native handler', JSON.stringify({ retVal: ex.Result.RetVal }));
          await this.hideVueBridgeIframe(TransitionType.DESTRUCT);
          this.postMessage(BridgeMessageFromType.SigninResult, {
            data: { success: false, handledNatively: true, errorCode: ex.Result.RetVal }
          }, message);
          await this.authProvider.handleAuthErrors(
            this.routeProvider.pages.SigninPage.page,
            ex,
            loginMethod,
            signinPayload,
            async () => { await this.completeNativeLogin(signinPayload, SegmentLocationNames.Login); }
          );
        } else {
          // Real signin error
          const errorCode = (ex && ex.errorCode) || (ex && ex.Result && ex.Result.RetVal);
          const errorMessage = (ex && (ex.errorMessage || ex.friendlyMessage || ex.message)) || (ex && ex.Result && ex.Result.RetMsg);
          LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Signin failed', JSON.stringify({
            status: ex && ex.status,
            errorCode: errorCode,
            errorMessage: errorMessage,
            keys: ex ? Object.keys(ex) : [],
          }));
          this.postMessage(BridgeMessageFromType.SigninResult, {
            data: {
              success: false,
              error: errorMessage || 'Signin failed',
              errorCode: errorCode,
              status: ex && ex.status,
              response: {
                status: (ex && ex.status) || 0,
                data: (ex && ex.error) || null
              }
            }
          }, message);
        }
      }
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Failed to handle signin');
      this.postMessage(BridgeMessageFromType.SigninResult, {
        data: { success: false, error: 'Internal error during signin' }
      }, message);
    }
  }

  async handleValidateOTP(message) {
    try {
      LoggerProvider.info(this, 'Handling validateOTP request from Vue', JSON.stringify(message.data));
      const { code, deliveryType, securityCode, sessionKey, action } = message.data;

      const mfaDeviceToken = await this.loginProvider.getMfaDeviceToken();
      const otpAction = action || 80;

      const data = {
        'securityCode': securityCode,
        'sessionKey': sessionKey,
        'action': otpAction,
        'code': code,
        'DeviceId': LoggerProvider.uuid,
        'DeviceSimCard': mfaDeviceToken,
        'DeliveryType': deliveryType,
      };

      let response;
      if (this.pendingEmailOtp) {
        const referenceValue = message.data.referenceValue || this.pendingEmailOtp.referenceValue;
        const secCode = securityCode || this.pendingEmailOtp.securityCode;
        response = await this.loginProvider.validateSecurityQuestionsByOtp(referenceValue, OTP_ACTIONS.PASS_SECURITY_QUESTIONS, code, DELIVERY_TYPE.EMAIL, secCode);
      } else {
        const url = APP_CONFIG.API_URL + (APP_CONFIG.featureFlags.signinOld
          ? 'api/Auth/ValidateOtp'
          : 'api/Auth/V3/ValidateOtp');

        response = await this.httpProvider.post(url, data);
      }

      if (response && response.Result && response.Result.RetVal === 1) {
        if (response.Value.VerifiedDevice) {
          HttpProvider.deviceToken = response.Value.VerifiedDevice;
          this.storageProvider.set(StorageType.DeviceToken, response.Value.VerifiedDevice);
        }
        this.pendingEmailOtp = null;

        const username = this.pendingLoginUsername;
        await this.authProvider.proceedOtpLogin(
          { username: username },
          response.Value
        );

        this.pendingSigninResult = AuthMapper.fromSigninEntity(response.Value);
        this.pendingSigninResult.password = this.pendingLoginPassword;

        this.postMessage(BridgeMessageFromType.ValidateOTPResult, {
          data: {
            success: true,
            result: {
              token: response.Value.access_token,
              tokenType: response.Value.token_type,
              refreshToken: response.Value.refresh_token,
              expiresIn: response.Value.expires_in,
              deviceToken: response.Value.VerifiedDevice,
              requirePhoneUpdate: response.Value.PhoneUpdateRequired,
              remainingAttempts: response.Value.OTPRemainingAttempts,
            }
          }
        }, message);
      } else {
        this.postMessage(BridgeMessageFromType.ValidateOTPResult, {
          data: {
            success: false,
            error: (response && response.Result) ? response.Result.RetMsg : 'OTP validation failed',
            remainingAttempts: response && response.Value ? response.Value.OTPRemainingAttempts : null,
            retVal: response && response.Result ? response.Result.RetVal : null,
          }
        }, message);
      }
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'ValidateOTP failed');
      this.postMessage(BridgeMessageFromType.ValidateOTPResult, {
        data: {
          success: false,
          error: ex.errorMessage || ex.message || 'OTP validation failed',
          errorCode: ex.errorCode,
          status: ex.status,
        }
      }, message);
    }
  }

  async handleResendOTP(message) {
    try {
      LoggerProvider.info(this, 'Handling resendOTP request from Vue');
      const { deliveryType, securityCode, referenceValue, action } = message.data;
      const response = await this.loginProvider.resendOtp(action, securityCode, referenceValue, deliveryType, this.pendingLoginUsername || '');
      this.postMessage(BridgeMessageFromType.ResendOTPResult, {
        data: { response }
      }, message);
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'ResendOTP failed');
      this.postMessage(BridgeMessageFromType.ResendOTPResult, {
        data: {
          error: ex.errorMessage || ex.message || 'OTP resend failed',
        }
      }, message);
    }
  }

  async handleRedirectFullRegistration(message) {
    try {
      LoggerProvider.info(this, 'Handling redirect-full-registration from Vue (OTP retVal 40 / open agent referral)');
      const data = (message.data || {}) as BridgeRedirectFullRegistrationData;
      const { openAgentReferral, sessionId, phoneNumber, phoneNumberValue, phoneNumberCountry, phoneNumberCountryIso, email, password, country } = data;

      if (!openAgentReferral) {
        LoggerProvider.error(this, LoggerEvent.Vue, 'redirect-full-registration missing open agent referral payload');
        this.postMessage(BridgeMessageFromType.RedirectFullRegistrationAck, { data: { success: false, error: 'Missing open agent referral' } }, message);
        return;
      }

      let register = this.registerProvider.getCurrentRegistration() || {};
      register = {
        ...register,
        phoneNumber: phoneNumber,
        phoneNumberValue: phoneNumberValue,
        phoneNumberCountry: phoneNumberCountry,
        phoneNumberCountryIso: phoneNumberCountryIso,
        emailAddress: email,
      };
      register.UserLogin = {
        DeviceId: null,
        DeviceVerificationId: null,
        ...(register.UserLogin || {}),
        SessionId: sessionId,
        Password: password,
      };
      if (!register.ProvidedFields)
        register.ProvidedFields = [];
      register.analyticsData = {
        ...(register.analyticsData || {}),
        mobilePhone: phoneNumber ? '+' + String(phoneNumber).replace('-', '') : (register.analyticsData ? register.analyticsData.mobilePhone : undefined),
        userCountry: country,
      };
      this.registerProvider.setCurrentRegistration(register);

      // Run the existing ARP recovery (prefill + agent attribution)
      try {
        await this.registerProvider.handleOpenAgentReferral(openAgentReferral, sessionId);

        const updated = this.registerProvider.getCurrentRegistration();
        if (updated && updated.analyticsData && updated.analyticsData.userCountry && updated.analyticsData.userCountry !== APP_CONFIG.region.defaultCountryFrom) {
          LoggerProvider.info(this, `Country changed from ${APP_CONFIG.region.defaultCountryFrom} to ${updated.analyticsData.userCountry} - reloading country features`);
          await this.authProvider.setUserCountry(updated.analyticsData.userCountry, true, true, true, false, false);
          this.utilsProvider.refreshCountryFeatures();
        }
      } catch (ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'handleOpenAgentReferral failed during bridge redirect');
      }

      const finalRegister = this.registerProvider.getCurrentRegistration();
      if (!finalRegister.ProvidedFields)
        finalRegister.ProvidedFields = [];
      const emailField = finalRegister.ProvidedFields.find((field) => field.FieldId === '18001');
      if (emailField)
        emailField.Value = finalRegister.emailAddress;
      else
        finalRegister.ProvidedFields.push({ FieldId: '18001', Value: finalRegister.emailAddress });
      this.registerProvider.setCurrentRegistration(finalRegister);

      this.postMessage(BridgeMessageFromType.RedirectFullRegistrationAck, { data: { success: true } }, message);

      await this.hideVueBridgeIframe(TransitionType.DESTRUCT);
      /**
      * Supply the referral specific required fields, the same way registration-deeplinking-step
      * does. Without them the dynamic page falls back to the generic set, so the address step does
      * not carry field 4501 and the Loqate street lookup is never wired up.
      */
      await this.routeProvider.push(this.routeProvider.pages.RegistrationStepDynamicPage, {
        fieldsPromise: openAgentReferral.Token
          ? this.registerProvider.getAgentReferralCustomerFields(openAgentReferral.Token)
          : undefined,
      });
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'redirect-full-registration failed');
      this.postMessage(BridgeMessageFromType.RedirectFullRegistrationAck, { data: { success: false, error: ex.message } }, message);
    }
  }

  async handleLoginComplete(message) {
    this.handlingLoginComplete = true;
    try {
      LoggerProvider.info(this, 'Handling login complete from Vue');

      const signinResult = this.pendingSigninResult;
      if (!signinResult) {
        throw new Error('No pending signin result');
      }
      if (this.pendingEmailOtp) {
        throw new Error('Email OTP verification pending');
      }

      this.loadingProvider.show();

      try {
        const result = await this.authProvider.initializeUser(false, false, false, true);
        if (!result) {
          throw new Error('Failed to initialize user after login');
        }

        // Save firstName to storage for biometric login greeting
        const firstName = this.authProvider.getUserFirstName();
        if (firstName) {
          this.storageProvider.set(StorageType.LastLoginFirstName, firstName);
        }

        // bridge login skips native sign-in; without LastLogin wallet passcode setup loops
        if (this.pendingLoginUsername) {
          await this.utilsProvider.updateLastLogin(this.pendingLoginUsername);
        }

        this.loadingProvider.hide();

        const postLoginCallback = this.pendingPostLoginCallback;
        const requirePhoneUpdate = !!(signinResult && signinResult.authenticationResult && signinResult.authenticationResult.requirePhoneUpdate);

        this.postMessage(BridgeMessageFromType.LoginCompleteAck, {
          success: true,
          message: 'Login successful',
          customerId: HttpProvider.getAuthCustomerId(),
          requirePhoneUpdate,
        });

        // Rmt5VueBridgeLogin only means "RMT5 renders the login screen" - never deferred.
        await this.authProvider.checkPermissions();

        LoggerProvider.info(this, 'Login complete - navigating to app');

        this.pendingPostLoginCallback = null;
        if (postLoginCallback) {
          LoggerProvider.info(this, 'Login complete - running post-login callback (origin flow)');
          await postLoginCallback();
          return;
        }

        await this.authProvider.navigateLogin(signinResult, SegmentLocationNames.Login, true);

        if (this.isPostLoginVueHome()) {
          return;
        }

        await this.waitForPostLoginPageReady(20000);

        await this.hideVueBridgeIframe(TransitionType.DESTRUCT);

      } catch (loginError) {
        LoggerProvider.exception(this, loginError, LoggerEvent.Vue, 'Login complete failed', loginError);

        this.postMessage(BridgeMessageFromType.LoginCompleteAck, {
          success: false,
          error: loginError.message || 'Login failed'
        });

        RmtPopupComponent.showError('Login failed - please try again');
        this.loadingProvider.hide();
      }
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Failed to handle login complete');
      RmtPopupComponent.showError('Login failed - please try again');
      this.loadingProvider.hide();
    } finally {
      this.handlingLoginComplete = false;
      // Clean up pending state
      this.pendingSigninResult = null;
      this.pendingLoginUsername = null;
      this.pendingLoginPassword = null;
      this.pendingEmailOtp = null;
    }
  }

  // Older bundles don't send the signup marketing choice; without it the lite identify
  // derives the consent from the market alone, the same as RMT5 web.
  getRegistrationMarketingOptIn(registrationData): boolean {
    return !(registrationData && registrationData.receiveEmailMarketing === false);
  }

  async handleRegistrationComplete(message) {
    try {
      LoggerProvider.info(this, 'Handling registration complete from Vue', JSON.stringify(message.data));
      this.deeplinkProvider.clearDeeplinkReferral();

      await this.clearWelcomePendingCountries();

      const { username, password, registrationData } = message.data;
      
      // Validate required fields
      if (!username || !password) {
        throw new Error('Username and password are required for login after registration');
      }
      
      // Check for device token - it might be missing on some platforms
      if (!registrationData || !registrationData.deviceToken) {
        LoggerProvider.info(this, 'Device token missing in registration data:', registrationData);
        
        // Try alternative field names
        const deviceToken = (registrationData && registrationData.DeviceToken) || 
                          (registrationData && registrationData.device_token) || 
                          (registrationData && registrationData.mfaDeviceToken) ||
                          null;
        
        if (deviceToken) {
          LoggerProvider.info(this, 'Found device token in alternative field');
          registrationData.deviceToken = deviceToken;
        } else {
          LoggerProvider.error(this, LoggerEvent.Vue, 'No device token found in registration response - proceeding without it');
        }
      }
      
      // Store device token BEFORE attempting login
      HttpProvider.deviceToken = registrationData.deviceToken;
      await this.storageProvider.set(StorageType.DeviceToken, registrationData.deviceToken);
      LoggerProvider.info(this, 'Device token set from registration:', registrationData.deviceToken);
      
      // Verify token was stored correctly
      const storedToken = await this.storageProvider.get(StorageType.DeviceToken);
      
      // Store any other registration data that might be needed
      if (registrationData.customerId) {
        LoggerProvider.info(this, 'Registration completed for customer ID:', registrationData.customerId);
      }
      
      // Show loading while logging in
      this.loadingProvider.show();
      
      try {
        // DO NOT clear auth tokens - we need the session token for signin!
        // Determine login method (email or phone)
        const loginMethod = this.utilsProvider.checkAndReturnLoginMethod(username);
        
        // Build signin payload matching the registration-finish pattern
        // IMPORTANT: The sessionId from registration must be passed to link the registration session
        const signinPayload = {
          username: username,
          password: password,
          deviceId: LoggerProvider.uuid,
          deviceToken: registrationData.deviceToken,
          // The sessionId from registration MUST be included to link the sessions
          sessionId: registrationData.sessionId,
        };

        // Check if there's a captcha session from registration or message
        let captchaSession = null;
        
        // First check registration data
        if (registrationData && (registrationData.captchaSession || registrationData.captchaToken || registrationData.CaptchaSession)) {
          captchaSession = registrationData.captchaSession || registrationData.captchaToken || registrationData.CaptchaSession;
        }
        // Then check message level
        else if (message.captchaSession || message.session) {
          captchaSession = message.captchaSession || (message.session && message.session.captchaSession);
        }
        // If no captcha session found, we need to get one
        else {
          try {
            // Get a new captcha session token using the static method
            captchaSession = await XeCaptchaComponent.getTokenStatic(
              this.scriptProvider, 
              this.httpProvider, 
              SegmentLocationNames.Registration
            );
          } catch (error) {
            console.error(error);
          }
        }

        // Use Angular's native login flow
        const signinResult = await this.authProvider.signin(
          signinPayload, 
          loginMethod,
          captchaSession,
          SegmentLocationNames.Registration
        );
        
        if (signinResult) {
          LoggerProvider.info(this, 'Login successful after registration');

          if (registrationData.customerProfile || registrationData.analyticsData) {
            const analyticsData = registrationData.analyticsData || registrationData.customerProfile;
            const customerId = HttpProvider.getAuthCustomerId(null, true);

            SegmentProvider.currentUserId = customerId;
            this.segmentProvider.identifyCustomerByAnalyticsData({
              ...analyticsData,
              id: customerId,
            }, false);
          }

          // Same args as registration-finish: RMT5 already sent the user's marketing choice in the create request
          const initResult = await this.authProvider.initializeUser(false, false, false, true, false, this.getRegistrationMarketingOptIn(registrationData));
          
          if (!initResult) {
            throw new Error('Failed to initialize user after registration');
          }

          const firstNameReg = this.authProvider.getUserFirstName();
          if (firstNameReg) {
            this.storageProvider.set(StorageType.LastLoginFirstName, firstNameReg);
          }

          // same as handleLoginComplete: bridge registration must write LastLogin
          await this.utilsProvider.updateLastLogin(username);

          // Hide loading before showing permission modals
          this.loadingProvider.hide();

          const requirePhoneUpdate = !!(signinResult && signinResult.authenticationResult && signinResult.authenticationResult.requirePhoneUpdate);

          // Send success confirmation back to Vue
          this.postMessage(BridgeMessageFromType.RegistrationCompleteAck, {
            success: true,
            message: 'Login successful',
            customerId: HttpProvider.getAuthCustomerId(),
            requirePhoneUpdate,
          });

          if (this.isRmt5OnboardingEnabled()) {
            if (!this.loginProvider.isLiteUser()) {
              await this.authProvider.acceptLTLicense();
            }
            LoggerProvider.info(this, 'Registration complete - RMT5 owns onboarding; keeping webview foregrounded');
            // don't let a stale finalize swallow this one
            this.onboardingFinalizeInFlight = false;
            this.pendingOnboardingSignin = signinResult;
            this.pendingOnboardingLocation = SegmentLocationNames.Registration;
            // checkPermissions() is skipped here, so the token stays orphaned without this.
            await this.authProvider.rebindPushRegistrationForSession();
            this.armOnboardingWatchdog();
            return;
          }

          // Keep the Vue bridge visible during permission flow
          LoggerProvider.info(this, 'Keeping Vue bridge visible during permission flow');
          
          // Check permissions (notifications and location) - these will appear as modals
          // over the Vue bridge iframe
          await this.authProvider.checkPermissions();
          
          // Accept LT license if not a lite user
          if (!this.loginProvider.isLiteUser()) {
            await this.authProvider.acceptLTLicense();
          }

          // This will handle biometrics enrollment if available
          await this.authProvider.navigateLogin({ ...signinResult, password }, SegmentLocationNames.Registration, true);

          if (this.isPostLoginVueHome()) {
            return;
          }

          // Hide the Vue bridge iframe only after the post-login page is ready
          await this.waitForPostLoginPageReady(20000);
          // Use destruct after successful registration/login
          await this.hideVueBridgeIframe(TransitionType.DESTRUCT);
          
        } else {
          throw new Error('Login failed after registration - no signin result');
        }
        
      } catch (loginError) {
        LoggerProvider.exception(this, loginError, LoggerEvent.Vue, 'Login failed after registration', loginError);

        this.loadingProvider.hide();
        
        let errorMessage = 'Failed to complete login after registration. Please try logging in manually.';
        let requiresManualLogin = true;
        
        if (loginError.status === 307 || (loginError.message && loginError.message.includes('307'))) {
          LoggerProvider.error(this, LoggerEvent.Vue, 'Session/redirect issue - 307 redirect');
          errorMessage = 'Authentication service temporarily unavailable. Please try logging in manually.';
        } else if (loginError.status === 401) {
          errorMessage = 'Invalid credentials. Please try logging in manually.';
        } else if (loginError.status === 403) {
          errorMessage = 'Access denied. Please contact support.';
          requiresManualLogin = false;
        } else if (loginError.friendlyMessage) {
          errorMessage = loginError.friendlyMessage;
        }
        
        RmtPopupComponent.showError(errorMessage);
        
        // Send error back to Vue
        this.postMessage(BridgeMessageFromType.RegistrationCompleteAck, { 
          success: false, 
          error: loginError.message || 'Login failed',
          errorCode: loginError.status,
          requiresManualLogin: requiresManualLogin
        });
        
        // Clear any partial auth state
        this.httpProvider.resetAuthToken();
      }
      
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Error handling registration complete');
      
      // Hide loading if it was shown
      this.loadingProvider.hide();
      
      // Show error to user
      RmtPopupComponent.showError('An error occurred during registration completion. Please try again.');
      
      // Send error back to Vue
      this.postMessage(BridgeMessageFromType.RegistrationCompleteAck, { 
        success: false, 
        error: ex.message || 'Unknown error' 
      });
    }
  }

  async handleOnboardingComplete(message) {
    LoggerProvider.info(this, 'Handling onboarding complete from Vue');
    await this.finalizeOnboarding({ ranByRmt5: true });
  }

  private armOnboardingWatchdog() {
    this.clearOnboardingWatchdog();
    this.onboardingWatchdogTimer = window.setTimeout(() => {
      this.onboardingWatchdogTimer = null;
      if (!this.pendingOnboardingSignin) {
        return; // onboarding-complete or a cancel already consumed it
      }
      LoggerProvider.error(this, LoggerEvent.Vue, 'Onboarding watchdog fired - RMT5 never signalled onboarding-complete; completing login natively');
      this.finalizeOnboarding({ ranByRmt5: false, force: true }).catch((ex) =>
        LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Onboarding watchdog fallback failed'));
    }, this.onboardingWatchdogMs);
  }

  private clearOnboardingWatchdog() {
    if (this.onboardingWatchdogTimer) {
      window.clearTimeout(this.onboardingWatchdogTimer);
      this.onboardingWatchdogTimer = null;
    }
  }

  // idempotent; force=true lets the watchdog bypass a stale in-flight flag
  private async finalizeOnboarding(opts: { ranByRmt5: boolean; force?: boolean }): Promise<void> {
    if (this.onboardingFinalizeInFlight && !opts.force) {
      LoggerProvider.info(this, 'finalizeOnboarding: already in flight - ignoring');
      return;
    }
    this.onboardingFinalizeInFlight = true;
    this.clearOnboardingWatchdog();

    const signin = this.pendingOnboardingSignin;
    const location = this.pendingOnboardingLocation || SegmentLocationNames.Login;
    this.pendingOnboardingSignin = null;
    this.pendingOnboardingLocation = null;

    try {
      if (!signin) {
        LoggerProvider.info(this, 'finalizeOnboarding: no pending signin - already finalized');
        return;
      }

      if (!opts.ranByRmt5) {
        LoggerProvider.error(this, LoggerEvent.Vue, 'Finalizing onboarding via native fallback - RMT5 did not complete onboarding');
        await this.authProvider.checkPermissions();
      }

      const routeBefore = this.currentVueRoute;
      await this.authProvider.navigateLogin(signin, location, !opts.ranByRmt5);

      // a changed route only means navigate() was called; let the native side decide
      const bridgeMoved = !!this.currentVueRoute && this.currentVueRoute !== routeBefore;
      const nativeMoved = await this.waitForPostLoginPageReady(bridgeMoved ? 2000 : 20000);
      if (bridgeMoved && !nativeMoved) {
        LoggerProvider.info(this, `finalizeOnboarding: bridge landed on ${this.currentVueRoute} - keeping it`);
        return;
      }
      await this.hideVueBridgeIframe(TransitionType.DESTRUCT);
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Failed to finalize onboarding');
      await this.hideVueBridgeIframe(TransitionType.DESTRUCT);
    } finally {
      this.onboardingFinalizeInFlight = false;
    }
  }

  async handleBankDepositComplete(message) {
    try {
      LoggerProvider.info(this, 'Handling bank deposit complete from Vue', JSON.stringify(message.data));

      const data = (message.data || {}) as BridgeBankDepositCompleteData;

      this.postMessage(BridgeMessageFromType.BankDepositCompleteAck, { success: true }, message);

      const callback = this.bankDepositCompleteCallback;
      this.bankDepositCompleteCallback = null;

      // no callback means the flow lives in the iframe; keep the bridge
      if (!callback) {
        LoggerProvider.info(this, 'No bank deposit complete callback registered - keeping the bridge on screen');
        return;
      }

      await Promise.resolve(callback(data));
      await this.endBankDepositOverlay();
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Error handling bank deposit complete');
      this.postMessage(BridgeMessageFromType.BankDepositCompleteAck, {
        success: false,
        error: ex.message || 'Unknown error',
      }, message);
      await this.endBankDepositOverlay();
    }
  }

  private async endBankDepositOverlay() {
    const restoreRoute = this.restoreVueRouteAfterBankDeposit;
    this.restoreVueRouteAfterBankDeposit = null;
    this.elevateIframe = false;
    if (restoreRoute) {
      if (this.container) {
        this.container.style.zIndex = '1';
      }
      this.navigate(restoreRoute, '', { replace: true });
      return;
    }
    await this.hideVueBridgeIframe(TransitionType.DESTRUCT);
    VueBridgeState.setUserInsideVueBridge(false);
  }

  async handleCompleteProfileComplete(message) {
    try {
      LoggerProvider.info(this, 'Handling complete profile from Vue', JSON.stringify(message.data));

      const { updatedFields, updateResponse } = message.data;

      // Validate response
      if (!updateResponse) {
        throw new Error('Update response is required');
      }

      // Store device token if provided
      if (updateResponse.deviceToken) {
        HttpProvider.deviceToken = updateResponse.deviceToken;
        await this.storageProvider.set(StorageType.DeviceToken, updateResponse.deviceToken);
        LoggerProvider.info(this, 'Device token updated from profile completion:', updateResponse.deviceToken);
      }

      const hasCustomCallback = !!VueBridgeState.onCompleteCallback;
      // default flow only; RMT5 shows its own loader on the wallet path
      if (!hasCustomCallback && !this.walletProvider.isWalletSupportEnabled())
        this.loadingProvider.show();

      try {
        // Refresh token/claims so lite users become full customers
        await this.httpProvider.refresh(false, true, RefreshUserDetails.Full);

        // Refresh customer data to get updated profile
        await this.loginProvider.infoClient();

        // Ensure the profile completed flag is set
        // The Vue app has confirmed the profile update succeeded
        if (HttpProvider.user) {
          HttpProvider.user.IsProfileCompleted = true;
        }

        const firstName = this.authProvider.getUserFirstName();
        if (firstName) {
          this.storageProvider.set(StorageType.LastLoginFirstName, firstName);
        }

        // Reinitialize resources to get updated features/limits
        await this.resourceProvider.initializeResources(true, true);

        // Send success acknowledgment
        this.postMessage(BridgeMessageFromType.CompleteProfileCompleteAck, {
          success: true,
          message: 'Profile updated successfully',
          customerId: HttpProvider.getAuthCustomerId()
        });

        LoggerProvider.info(this, 'Complete profile successful');

        // Track analytics
        const customerId = HttpProvider.getAuthCustomerId(null, true);
        this.segmentProvider.trackEvent({
          eventName: 'Profile Completed',
          analyticsData: {
            customerId: customerId,
            location: 'complete_profile_vue',
            updatedFields: updatedFields ? Object.keys(updatedFields) : []
          }
        });

        await this.continueAfterProfileCompletion();

      } catch (updateError) {
        this.loadingProvider.hide();

        LoggerProvider.exception(this, updateError, LoggerEvent.Vue, 'Error refreshing profile after complete');

        // Send error acknowledgment
        this.postMessage(BridgeMessageFromType.CompleteProfileCompleteAck, {
          success: false,
          error: updateError.message || 'Failed to update profile',
          errorCode: updateError.status
        });

        // Show error alert
        RmtPopupComponent.showError('We couldn\'t update your profile. Please try again.');

        // Hide iframe anyway
        await this.hideVueBridgeIframe(TransitionType.DESTRUCT);
      }

    } catch (ex) {
      this.loadingProvider.hide();
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Unexpected error in handleCompleteProfileComplete');

      // Send error acknowledgment
      this.postMessage(BridgeMessageFromType.CompleteProfileCompleteAck, {
        success: false,
        error: ex.message || 'Unknown error'
      });

      // Show error
      RmtPopupComponent.showError('An unexpected error occurred. Please try again.');

      // Hide iframe
      await this.hideVueBridgeIframe(TransitionType.DESTRUCT);
    }
  }

  private async continueAfterProfileCompletion(): Promise<void> {
    // Check if there's a custom callback (e.g., from send flow)
    if (VueBridgeState.onCompleteCallback) {
      LoggerProvider.info(this, 'Executing custom onComplete callback');
      const callback = VueBridgeState.onCompleteCallback;
      VueBridgeState.onCompleteCallback = null; // Clear the callback

      // Execute the callback (e.g., continue with send flow)
      await Promise.resolve(callback());

      // Wait for the recipient screen to be ready before hiding the iframe
      await this.waitForRecipientFlowReady(15000);

      // Hide the Vue bridge iframe after next screen is shown
      await this.hideVueBridgeIframe(TransitionType.DESTRUCT);
    } else if (this.walletProvider.isWalletSupportEnabled()) {
      // US wallet onboarding isn't finished; going home would restart it
      LoggerProvider.info(this, 'Routing within Vue bridge to: WalletOnboardingContinue');
      this.loadingProvider.hide();
      await this.navigate('WalletOnboardingContinue');
    } else {
      // Try Vue Home first; only destruct the iframe if we actually fall back to native.
      const landedOnVueHome = await this.navigateToHomeOrFallback(this.routeProvider.pages.TabsHomeActivityPage);
      if (!landedOnVueHome) {
        await this.hideVueBridgeIframe(TransitionType.DESTRUCT);
      }

      this.loadingProvider.hide();
    }
  }

  async handleIdentify(message) {
    await this.analytics.identify(message.data.userId, message.data.traits);
  }

  async handleTrackEvent(message) {
    await this.trackBridgeEvent(message.data.event, message.data.traits);
  }

  async handlePage(message) {
    await this.trackBridgeEvent(message.data.pageName, message.data.traits);
  }

  private async trackBridgeEvent(eventName: string, traits: any) {
    const analyticsData = traits || {};
    analyticsData.productVariant = analyticsData.productVariant || 'RMT5';
    try {
      await this.analytics.trackEvent({
        eventName,
        analyticsData
      });
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, `Failed to track bridge event ${eventName}`);
    }
  }

  async handleHttp(type, message) {
    let url = message.data.url
    if (!url) {
      const response = {
        data: {
          error: `Missing URL ${type}`
        }
      };
      this.postMessage(BridgeMessageFromType.HttpError, response, message);
      return;
    }
    const stripTrailingSlashes = (s: string) => s.replace(/\/+$/, '');
    const ensureLeadingSlash = (s: string) => (s.startsWith('/') ? s : `/${s}`);
    if (message.data.apiType === BridgeAPIType.CST)
      url = `${stripTrailingSlashes(APP_CONFIG.API_URL)}/api${ensureLeadingSlash(url)}`;
    else if (message.data.apiType === BridgeAPIType.RMT)
      url = `${stripTrailingSlashes(APP_CONFIG.remittanceApiBaseUrl)}${ensureLeadingSlash(url)}`;
    else if (message.data.apiType === BridgeAPIType.Logs)
      url = `${stripTrailingSlashes(APP_CONFIG.logsApiUrl)}${ensureLeadingSlash(url)}`;
    else {
      const response = {
        data: {
          error: `Unknown API type ${message.data.apiType} for ${type} ${url}`
        }
      };
      this.postMessage(BridgeMessageFromType.HttpError, response, message);
      return;
    }

    // this.capturePendingBridgeLoginUsername(url, message && message.data ? message.data.data : null);
      
    let result = null;
    let statusCode = 200;
    let responseHeaders = {};
    // The json serializer throws on a null body, so a body-less POST/PUT would never be sent.
    const body = message.data.data === null || message.data.data === undefined ? {} : message.data.data;

    try {
      let request: Promise<any>;
      if (type === BridgeMessageToType.HttpRequestGet) {
        request = this.httpProvider.get(url, message.data.queryParams, null, message.data.headers, true);
      } else if (type === BridgeMessageToType.HttpRequestPost) {
        request = this.httpProvider.post(url, body, null, message.data.headers, true);
      } else if (type === BridgeMessageToType.HttpRequestPut) {
        request = this.httpProvider.put(url, body, null, message.data.headers, true);
      } else if (type === BridgeMessageToType.HttpRequestPatch) {
        request = this.httpProvider.patch(url, body, null, message.data.headers, true);
      } else if (type === BridgeMessageToType.HttpRequestDelete) {
        request = this.httpProvider.delete(url, message.data.queryParams, null, message.data.headers, true);
      }
      if (request) {
        result = await this.withBridgeHttpBudget(request, `${type} ${url}`);
      } else {
        const response = {
          data: {
            error: `Unknown http function ${type}`
          }
        };
        this.postMessage(BridgeMessageFromType.HttpError, response, message);
        return;
      }
    } catch (ex) {
      if (ex && ex.status >= 300 && ex.status < 400) {
        statusCode = ex.status;

        if (ex.headers) {
          const headers = {};
          if (this.platform.is('cordova')) {
            const headerKeys = Object.keys(ex.headers);
            headerKeys.forEach(key => {
              headers[key.toLowerCase()] = this.httpProvider.getHeadersValue(ex.headers, key);
            });
          } else {
            ex.headers.keys().forEach((key: string) => {
              headers[key.toLowerCase()] = ex.headers.get(key);
            });
          }
          responseHeaders = headers;
        }

        result = ex.error || {};
      } else {
        const response = {
          data: {
            response: {
              status: (ex && ex.status) || 0,
              headers: {},
              data: (ex && ex.error) || { error: `Http failed for ${type} ${url}` }
            }
          }
        };
        this.postMessage(BridgeMessageFromType.HttpError, response, message);
        return;
      }
    }

    if (statusCode === 200 && this.isPasswordChange(type, message.data.apiType, url, body, result))
      await this.authProvider.clearBiometricsAfterPasswordChange();

    // TODO !!!!!!!!!!!!!!!! what about error messages like a 400 for /validate ?
    const response = {
      data: {
        response: {
          status: statusCode,
          headers: responseHeaders,
          data: result
        }
      }
    };
    this.postMessage(BridgeMessageFromType.HttpResponse, response, message);
  }

  private isPasswordChange(type: string, apiType: string, url: string, body: any, result: any): boolean {
    const path = (url || '').split('?')[0];
    if (type === BridgeMessageToType.HttpRequestPost && apiType === BridgeAPIType.CST && /\/account\/forgotPassword\/v2$/i.test(path))
      return !!(body && body.password && body.referenceValue && result && result.Result && result.Result.RetVal === FORGOT_PASSWORD_RET_VAL.SUCCESS);
    return type === BridgeMessageToType.HttpRequestPut && apiType === BridgeAPIType.RMT && /\/customer-profile\/password$/i.test(path);
  }

  // Reply before the webview's 60 s bridge timeout; a late reply is dropped.
  private async withBridgeHttpBudget(request: Promise<any>, label: string): Promise<any> {
    let timer: number;
    const budget = new Promise((resolve, reject) => {
      timer = window.setTimeout(() => {
        LoggerProvider.error(this, LoggerEvent.Vue, `Bridge HTTP exceeded ${VueProvider.BRIDGE_HTTP_BUDGET_MS} ms budget for ${label}`);
        reject({ status: 0, error: { error: `Native request timed out for ${label}` } });
      }, VueProvider.BRIDGE_HTTP_BUDGET_MS);
    });
    try {
      return await Promise.race([request, budget]);
    } finally {
      window.clearTimeout(timer);
    }
  }

  async handleSyncData(message) {
    if (message.data.countryId) {
      this.httpProvider.updateAuthCountry(message.data.countryId);
    }
    if (message.data.user) {
      // TODO !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
    }
    if (message.data.resources) {
      // TODO !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
    }
  }

  isNativeType(messageType, nativeType) {
    for (const type in nativeType) {
      if (messageType === nativeType[type])
        return true;
    }
    return false;
  }

  async handleNative(message) {
    if (this.isNativeType(message.data.type, BridgeNativeType.Biometrics)) {
      await this.handleBiometricsNative(message);
      return;
    }

    if (message.data.type === BridgeNativeType.Adyen.Handle3DSRedirect) {
      this.handleAdyen3DSRedirect(message);
      return;
    }

    if (message.data.type === BridgeNativeType.File.SharePdf) {
      await this.handleSharePdf(message);
      return;
    }

    if (message.data.type === BridgeNativeType.File.DiscardPdf) {
      await this.handleDiscardPdf(message);
      return;
    }

    if (this.isNativeType(message.data.type, BridgeNativeType.Contacts)) {
      await this.handleContacts(message);
      return;
    }

    if (this.isNativeType(message.data.type, BridgeNativeType.AdsTracking)) {
      try {
        const data = await this.handleAdsTracking(message.data.type, message.data);
        this.postMessage(BridgeMessageFromType.NativeFinished, { data }, message);
      } catch (ex) {
        const errorResponse = {
          data: {
            error: ex && ex.message ? ex.message : 'Failed to process ads tracking request',
          },
        };
        this.postMessage(BridgeMessageFromType.NativeError, errorResponse, message);
      }
      return;
    }

    if (this.isNativeType(message.data.type, BridgeNativeType.LiquidGlassBar)) {
      try {
        const data = await this.nativeProvider.liquidGlassBar.processNativeRequest(message.data.type, message.data);
        this.postMessage(BridgeMessageFromType.NativeFinished, { data }, message);
      } catch (ex) {
        this.postMessage(BridgeMessageFromType.NativeError, {
          data: { error: ex && ex.message ? ex.message : 'Liquid glass bar request failed' },
        }, message);
      }
      return;
    }

    if (this.isNativeType(message.data.type, BridgeNativeType.Permissions)) {
      try {
        const data = await this.handlePermissions(message.data.type);
        this.postMessage(BridgeMessageFromType.NativeFinished, { data }, message);
      } catch (ex) {
        this.postMessage(BridgeMessageFromType.NativeError, {
          data: { error: ex && ex.message ? ex.message : 'Failed to process permissions request' },
        }, message);
      }
      return;
    }

    if (this.isNativeType(message.data.type, BridgeNativeType.Inbox)) {
      try {
        const data = await this.handleInbox(message.data.type, message.data);
        this.postMessage(BridgeMessageFromType.NativeFinished, { data }, message);
      } catch (ex) {
        const errorResponse = {
          data: {
            error: ex && ex.message ? ex.message : 'Failed to process inbox request',
          },
        };
        this.postMessage(BridgeMessageFromType.NativeError, errorResponse, message);
      }
      return;
    }

    if (this.isNativeType(message.data.type, BridgeNativeType.Payments)) {
      try {
        if (message.data.type === BridgeNativeType.Payments.AddBankAccount) {
          const allowed = await this.paymentProvider.isBankAccountAddAllowed();
          if (allowed !== BankAccountAddAllowed.Allowed) {
            throw new Error('Bank account add not allowed (limit reached or unsupported region)');
          }
          const pushAddPage = () => {
            this.routeProvider.openModal(this.routeProvider.pages.PaymentAccountAddPage, {
              paymentMethod: PaymentMethodType.BankAccount,
              onlyBankAccounts: true,
              location: 'bridge',
            });
          };
          if (APP_CONFIG.region.isPlaidEnabled) {
            const returnToBridge = () => {
              this.navigate('AccountPaymentMethods', '', { replace: true }, () => {
                // fallback: land on the native list if the bridge push fails
                this.routeProvider.popToOrPush(this.routeProvider.pages.PaymentAccountListPage, { paymentMethod: PaymentMethodType.BankAccount });
              });
            };
            this.plaidProvider.plaidNative({ location: 'bridge', onBridgeReturn: returnToBridge });
          } else {
            pushAddPage();
          }
          this.postMessage(BridgeMessageFromType.NativeFinished, { data: { ok: true } }, message);
          return;
        }
        throw new Error(`Unknown payments native type: ${message.data.type}`);
      } catch (ex) {
        this.postMessage(BridgeMessageFromType.NativeError, {
          data: { error: ex && ex.message ? ex.message : 'Failed to process payments request' },
        }, message);
      }
      return;
    }

    if (this.isNativeType(message.data.type, BridgeNativeType.Receipt)) {
      try {
        if (message.data.type === BridgeNativeType.Receipt.View) {
          const orderNumber = message.data.request && message.data.request.orderNumber;
          if (!orderNumber)
            throw new Error('orderNumber is required to view a receipt');
          const file: any = await this.receiptProvider.getOrderReceipt(orderNumber);
          if (!file || !file.nativeURL)
            throw new Error('Receipt download returned no file');
          await this.fileOpener.open(file.nativeURL, 'application/pdf');
          this.postMessage(BridgeMessageFromType.NativeFinished, { data: { ok: true } }, message);
          return;
        }
        throw new Error(`Unknown receipt native type: ${message.data.type}`);
      } catch (ex) {
        this.postMessage(BridgeMessageFromType.NativeError, {
          data: { error: ex && ex.message ? ex.message : 'Failed to open receipt' },
        }, message);
      }
      return;
    }

    if (this.isNativeType(message.data.type, BridgeNativeType.Accertify)) {
      try {
        if (message.data.type === BridgeNativeType.Accertify.GetDeviceId) {
          let accertifyDeviceId = await this.orderProvider.getAccertifyTransactionId();
          if (!accertifyDeviceId) {
            await Promise.race([
              this.orderProvider.initializeAccertify(),
              new Promise((resolve) => setTimeout(resolve, VueProvider.ACCERTIFY_PULL_TIMEOUT_MS)),
            ]);
            accertifyDeviceId = OrderProvider.AccertifyTransactionID;
          }
          this.postMessage(BridgeMessageFromType.NativeFinished, { data: { accertifyDeviceId: accertifyDeviceId || null } }, message);
          return;
        }
        throw new Error(`Unknown accertify native type: ${message.data.type}`);
      } catch (ex) {
        this.postMessage(BridgeMessageFromType.NativeError, {
          data: { error: ex && ex.message ? ex.message : 'Failed to get Accertify device id' },
        }, message);
      }
      return;
    }

    const response = {
      data: {
        error: `Unknown native function ${message.data.type}`
      }
    };
    this.postMessage(BridgeMessageFromType.NativeError, response, message);
  }

  // The `replied` latch guarantees exactly one reply; a cancelled prompt used to answer
  // neither way and leave the RMT5 button dead.
  private async handleBiometricsNative(message): Promise<void> {
    let replied = false;
    const finish = (data) => {
      if (replied) {
        return;
      }
      replied = true;
      this.postMessage(BridgeMessageFromType.NativeFinished, { data }, message);
    };
    const fail = (error: string) => {
      if (replied) {
        return;
      }
      replied = true;
      this.postMessage(BridgeMessageFromType.NativeError, { data: { error } }, message);
    };

    try {
      // RMT5 wallet messages with no dispatcher branch
      if (message.data.type === BridgeNativeType.Biometrics.Enable) {
        replied = true;
        await this.handleBiometricsEnable(message);
        return;
      }
      if (message.data.type === BridgeNativeType.Biometrics.Skip) {
        replied = true;
        await this.handleBiometricsSkip(message);
        return;
      }
      // USL scans arrive from routes other than LoginBiometrics, so the webview
      // flags them with performLogin instead of relying on the current route.
      const isLoginScan = message.data.type === BridgeNativeType.Biometrics.Scan
        && (this.currentVueRoute === 'LoginBiometrics' || !!(message.data.request && message.data.request.performLogin));
      if (isLoginScan) {
        // handleBiometricLoginScan owns its own reply on every path.
        replied = true;
        await this.handleBiometricLoginScan(message);
        return;
      }

      if (message.data.type === BridgeNativeType.Biometrics.Save && !(message.data.request && message.data.request.token)) {
        const outcome = await this.enableBiometricsForBridge(message.data.request && message.data.request.password);
        if (outcome.error) {
          fail(outcome.error);
          return;
        }
        finish({ enabled: true });
        return;
      }

      // RMT5 nests the token fields under message.data.request
      finish(await this.nativeProvider.biometrics.processNativeRequest(message.data.type, message.data.request));
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Biometrics native request failed');
      fail(ex && ex.message ? ex.message : 'Biometrics request failed');
    } finally {
      // Backstop for any future branch that returns without answering; a no-op once replied.
      fail('Biometrics request did not complete');
    }
  }

  // Bounded so a wedged enable can't hold the bridge request open.
  private async enableBiometricsForBridge(password: string): Promise<{ error?: string }> {
    if (!password) {
      return { error: 'Password is required to enable biometrics' };
    }
    const timedOut = {};
    const result: any = await Promise.race([
      this.authProvider.enableBiometrics(this.translate, password, false),
      new Promise((resolve) => window.setTimeout(() => resolve(timedOut), this.biometricsSaveTimeoutMs)),
    ]);
    if (result === timedOut) {
      LoggerProvider.error(this, LoggerEvent.Vue, 'Biometrics enable did not settle within the bridge budget');
      return { error: 'Biometrics enable did not complete' };
    }
    if (!(result && result.enabled)) {
      return { error: (result && result.error) || 'Biometrics enable failed' };
    }
    return {};
  }

  async handleLogout() {
    try {
      await this.authProvider.logoutUser(this.routeProvider.pages.PreLoginPage, true);
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Logout handling failed');
      await this.hideVueBridgeIframe();
    }
  }

  /** True when the RMT5 login screen can take a relogin instead of the native SigninPage modal. */
  canShowVueRelogin(): boolean {
    const rmt5LoginEnabled = !!UtilsProvider.getFeatureFlagDataUnsafe(FeatureFlagTypes.Rmt5VueBridgeLogin) || this.isAnyWelcomeArmEnabled();
    return rmt5LoginEnabled && this.isConnected && !this.disabled && !this.navigateDisabled;
  }

  /** Shows the RMT5 login above whatever is on screen; resolves once native finished the login. A null username means no prefill. */
  showVueRelogin(username: string | null): Promise<VueReloginResult> {
    return new Promise<VueReloginResult>((resolve) => {
      const wasVisible = !!(this.container && this.container.style.display === 'block');
      const returnRoute = wasVisible ? this.currentVueRoute : null;
      this.pendingReloginResolve = resolve;
      HttpProvider.vueReloginInProgress = true;
      this.pendingPostLoginCallback = async () => {
        this.finishVueRelogin();
        let landed = false;
        if (returnRoute) {
          // navigate() itself settles only when RMT5 leaves the page; wait for the push instead
          this.navigate(returnRoute, '', { replace: true }, null, false);
          landed = await this.waitForTransition();
        }
        if (!landed) {
          await this.hideVueBridgeIframe(TransitionType.DESTRUCT);
        }
        resolve(true);
      };
      this.elevateIframe = true;
      this.navigate('Login', '', { username, replace: true }, () => {
        LoggerProvider.info(this, 'RMT5 relogin unavailable, handing back to the native SigninPage');
        this.cancelVueRelogin('native');
      }, false);
    });
  }

  /** Ends a relogin shown in RMT5 without a login: the caller logs the customer out or goes native. */
  cancelVueRelogin(result: VueReloginResult = false): void {
    const resolveRelogin = this.pendingReloginResolve;
    if (!resolveRelogin) {
      return;
    }
    this.pendingPostLoginCallback = null;
    this.finishVueRelogin();
    resolveRelogin(result);
  }

  private finishVueRelogin(): void {
    this.pendingReloginResolve = null;
    HttpProvider.vueReloginInProgress = false;
    this.lowerIframe();
  }

  private lowerIframe(): void {
    this.elevateIframe = false;
    if (this.container) {
      this.container.style.zIndex = '1';
    }
  }

  async handleLogoutNavigation(lastLogin: string) {
    await this.showVueBridgeIframe(false);

    const deviceToken = await this.storageProvider.get(StorageType.FingerprintDeviceToken);
    const biometricsAvailable = deviceToken && deviceToken.length > 0 && await this.authProvider.biometricsAvailable();
    if (biometricsAvailable) {
      this.currentVueRoute = 'LoginBiometrics';
      this.postMessage(BridgeMessageFromType.Push, {
        routeName: 'LoginBiometrics',
        backPage: '',
        locale: HttpProvider.currentLanguage,
        countryId: APP_CONFIG.region.regionId,
        ...await this.getLoginBiometricsData(lastLogin),
        replace: true,
      });
    } else {
      this.currentVueRoute = 'Welcome';
      this.postMessage(BridgeMessageFromType.Push, {
        routeName: 'Welcome',
        backPage: '',
        locale: HttpProvider.currentLanguage,
        countryId: APP_CONFIG.region.regionId,
        biometricsAvailable: false,
        replace: true,
      });
    }
  }

  // Call only once biometrics are enrolled. A failed auto-scan reason other than 'cancelled' means the
  // saved fingerprint can't be used; RMT5 decides where to send the customer (RMT4-8727).
  async getLoginBiometricsData(username: string, failureReason?: BiometricLoginFailureReason): Promise<VueNavigateData> {
    const firstName = this.authProvider.getUserFirstName() || await this.storageProvider.get(StorageType.LastLoginFirstName) || '';
    const unusable = !!failureReason && failureReason !== 'cancelled';
    return {
      biometricType: this.resolveBiometricType(),
      biometricsAvailable: !unusable,
      biometricsUnavailableReason: unusable ? failureReason : undefined,
      username,
      firstName,
    };
  }

  private resolveBiometricType(): 'faceId' | 'touchId' | 'fingerprint' {
    if (this.authProvider.faceId) {
      return 'faceId';
    }
    return this.platform.is('android') ? 'fingerprint' : 'touchId';
  }

  // RMT5 persists its once-per-install flag off `answered`, so a timeout must report false.
  async handlePermissions(type) {
    if (type === BridgeNativeType.Permissions.NotificationRequest) {
      const granted = await this.authProvider.requestNotificationPermissionBridge();
      const answered = !PermissionProvider.isPermissionRequestPending(StorageType.NotificationAccess);
      return { granted, answered };
    }
    if (type === BridgeNativeType.Permissions.LocationRequest) {
      const granted = await this.authProvider.requestLocationPermissionBridge();
      const answered = !PermissionProvider.isPermissionRequestPending(StorageType.LocationAccess);
      return { granted, answered };
    }
    throw new Error(`Unknown permissions native type: ${type}`);
  }

  async handleAdsTracking(type, data) {
    if (type === BridgeNativeType.AdsTracking.Get) {
      const adsTracking = await this.customerProvider.getAdsTracking();
      return { adsTracking };
    }

    if (type === BridgeNativeType.AdsTracking.Set) {
      const request = (data && data.request) || {};
      const newValue = !!request.adsTracking;
      const adsTracking = await this.customerProvider.setAdsTracking(newValue, 'settings notifications');
      return { adsTracking };
    }

    throw new Error(`Unknown ads-tracking native type: ${type}`);
  }

  async handleInbox(type, data) {
    if (type === BridgeNativeType.Inbox.GetMessages) {
      const messages = (await this.iterableInAppProvider.getAppMessages()) || [];
      const mapped = messages.map((m: any) => ({
        messageId: m.messageId,
        title: (m.inboxMetadata && m.inboxMetadata.title) || '',
        subtitle: (m.inboxMetadata && m.inboxMetadata.subtitle) || '',
        date: this.formatInboxDate(m.createdAt),
        read: !!m.read,
      }));
      return { messages: mapped };
    }

    if (type === BridgeNativeType.Inbox.MessageClick) {
      const request = (data && data.request) || {};
      const messageId = request.messageId;
      if (!messageId) {
        throw new Error('messageId missing in inbox-message-click request');
      }
      const messages = (await this.iterableInAppProvider.getAppMessages()) || [];
      const full = messages.find((m: any) => m.messageId === messageId);
      if (!full) {
        throw new Error(`Inbox message not found: ${messageId}`);
      }
      await this.iterableInAppProvider.postInAppMessageOpen(messageId);
      this.routeProvider.openModal(this.routeProvider.pages.IterablesInappNotificationPage, {
        htmlContent: full.content && full.content.html,
        messageId,
        messageRead: full.read,
        inboxMetadata: full.inboxMetadata || '',
        campaignId: full.campaignId,
        entryPoint: CarouselCTAEntryPoint.Inbox,
      });
      return { ok: true };
    }

    throw new Error(`Unknown inbox native type: ${type}`);
  }

  private formatInboxDate(createdAt: string): string {
    if (!createdAt) return '';
    const d = new Date(createdAt);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  async handleBiometricLoginScan(message) {
    if (this.biometricLoginScanInProgress) {
      LoggerProvider.warning(this, LoggerEvent.Vue, 'Biometric login scan already in progress — rejecting duplicate request');
      this.postMessage(BridgeMessageFromType.NativeFinished, {
        data: { success: false, error: 'Biometric login already in progress' }
      }, message);
      return;
    }
    this.biometricLoginScanInProgress = true;
    try {
      const method = this.nativeProvider.biometrics.faceId ? 'faceID' : 'touchID';
      const { token: fingerprintToken, reason } = await this.authProvider.readStoredFingerprintToken(method, false);

      if (!fingerprintToken) {
        const data = reason === 'cancelled'
          ? { success: false, errorCode: 'cancelled', error: 'Biometric scan cancelled' }
          : { success: false, errorCode: reason, error: 'No fingerprint token found' };
        this.postMessage(BridgeMessageFromType.NativeFinished, { data }, message);
        return;
      }

      const deviceToken = await this.storageProvider.get(StorageType.FingerprintDeviceToken);
      const biometricsMethod = this.authProvider.getBiometricsMethod(method);
      const params = {
        biometricsMethod: biometricsMethod,
        fingerPrint: fingerprintToken,
        loginToken: fingerprintToken,
        deviceToken: deviceToken,
        deviceId: LoggerProvider.uuid,
      };

      try {
        await this.authProvider.signin(params, method, null, SegmentLocationNames.Login);
      } catch (ex) {
        await this.handleBiometricSigninError(ex, params, method, message);
        return;
      }

      const result = await this.authProvider.initializeUser(false, false, false, true);
      if (!result) {
        this.postMessage(BridgeMessageFromType.NativeFinished, {
          data: { success: false, error: 'Failed to initialize user' }
        }, message);
        return;
      }

      // Save firstName to storage for biometric login greeting
      const firstNameBio = this.authProvider.getUserFirstName();
      if (firstNameBio) {
        this.storageProvider.set(StorageType.LastLoginFirstName, firstNameBio);
      }

      this.postMessage(BridgeMessageFromType.NativeFinished, {
        data: { success: true }
      }, message);

      await this.authProvider.navigateLogin(params, SegmentLocationNames.Biometrics, false);
      if (this.isPostLoginVueHome()) {
        return;
      }
      await this.waitForPostLoginPageReady(20000);
      await this.hideVueBridgeIframe(TransitionType.DESTRUCT);

    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Biometric login scan failed');
      this.postMessage(BridgeMessageFromType.NativeFinished, {
        data: { success: false, error: ex.message || 'Biometric scan failed' }
      }, message);
    } finally {
      this.biometricLoginScanInProgress = false;
    }
  }

  private async handleBiometricSigninError(ex, params, method, message) {
    const status = ex && (ex.status || (ex.error && ex.error.status));
    const rmt5LoginEnabled = !!UtilsProvider.getFeatureFlagDataUnsafe(FeatureFlagTypes.Rmt5VueBridgeLogin);

    if (status === 307 && rmt5LoginEnabled) {
      LoggerProvider.info(this, 'Biometric login requires OTP, routing to RMT5 OTP screen');
      const otpData = this.extractOtpDataFromError(ex);
      this.pendingLoginUsername = await this.storageProvider.get(StorageType.LastLogin);
      this.pendingLoginPassword = null;
      this.postMessage(BridgeMessageFromType.NativeFinished, {
        data: {
          success: false,
          requireOtp: true,
          otpData: {
            maskedPhone: otpData.maskedPhone,
            maskedEmail: otpData.maskedEmail,
            referenceValue: otpData.referenceValue,
            securityCode: otpData.securityCode,
            otpCode: otpData.otpCode,
            waitingOtpTime: otpData.waitingOtpTime,
            sessionKey: otpData.sessionKey,
            remainingAttempts: otpData.OTPRemainingAttempts,
          }
        }
      }, message);
      return;
    }

    if (AuthProvider.isFingerprintTokenRejected(ex) && rmt5LoginEnabled) {
      // Stale/revoked fingerprint token — forward it as 'rejected' so RMT5 sends the
      // customer to the regular login instead of tearing down the webview for a native takeover.
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Biometric login signin failed');
      this.postMessage(BridgeMessageFromType.NativeFinished, {
        data: {
          success: false,
          errorCode: 'rejected',
          error: 'Signin failed',
          status: status,
          retVal: ex && ex.Result && ex.Result.RetVal,
          retMsg: ex && ex.Result && ex.Result.RetMsg,
        }
      }, message);
      return;
    }

    LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Biometric login signin failed, delegating to native handler');
    await this.hideVueBridgeIframe(TransitionType.DESTRUCT);

    this.postMessage(BridgeMessageFromType.NativeFinished, {
      data: { success: false, handledNatively: true }
    }, message);

    await this.authProvider.handleAuthErrors(
      this.routeProvider.pages.SigninPage.page,
      ex,
      method,
      params,
      async () => { await this.completeNativeLogin(params); }
    );
  }

  private extractOtpDataFromError(ex): any {
    const headers = ex && (ex.headers || (ex.error && ex.error.headers));
    const otpObject: any = {};
    if (headers) {
      const headerKeys = this.platform.is('cordova')
        ? Object.keys(headers)
        : (typeof headers.keys === 'function' ? headers.keys() : Object.keys(headers));
      headerKeys.forEach((key) => {
        const lowerKey = key.toLowerCase();
        const value = (!this.platform.is('cordova') && typeof headers.get === 'function')
          ? headers.get(key)
          : headers[key];
        otpObject[lowerKey] = (lowerKey === 'waitingotptime') ? Date.parse(value) : value;
      });
    }
    return AuthMapper.fromOtpHeader(otpObject);
  }

  private async completeNativeLogin(params, location: SegmentLocationNames = SegmentLocationNames.Biometrics) {
    const result = await this.authProvider.initializeUser(false, false, false, true);
    if (!result) {
      LoggerProvider.warning(this, LoggerEvent.Vue, 'Native-rescued login: initializeUser failed after OTP');
      RmtPopupComponent.showErrorWithLogTranslation('page.errorPage.loginInitializationFailed');
      return;
    }

    const firstNameBio = this.authProvider.getUserFirstName();
    if (firstNameBio) {
      this.storageProvider.set(StorageType.LastLoginFirstName, firstNameBio);
    }

    await this.authProvider.navigateLogin(params, location, false);
  }

  async handleLocalStorage(message) {
    const data = message.data;
    LoggerProvider.info(this, `Handling local storage request from Vue: ${data.action} ${data.key}`); 
    let responseData;
    try {
      switch(data.action) {
        case 'get':
          responseData = { 
            key: data.key, 
            value: await this.storageProvider.get(data.key)
          };
          break;
        case 'set':
          // An older bundle mirrors the flag as soon as the request resolves, timeout included.
          if (PermissionProvider.isPermissionRequestPending(data.key)) {
            LoggerProvider.info(this, `Ignoring local storage set for ${data.key} - the OS permission prompt is still unanswered`);
            responseData = { key: data.key, value: await this.storageProvider.get(data.key), ignored: true };
            break;
          }
          await this.storageProvider.set(data.key, data.value);
          responseData = { key: data.key, value: data.value };
          break;
        case 'remove':
          await this.storageProvider.remove(data.key);
          responseData = { key: data.key };
          break;
      }
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, `Error handling local storage request: ${data.action} ${data.key}`);
      responseData = { key: data.key, error: ex.message };
    }
    // RMT5 settles bridge requests off `data`, so a top-level spread arrived as undefined.
    // Needs the paired RMT5 change that reads `result.value` out of this envelope.
    this.postMessage(BridgeMessageFromType.LocalStorageResult, { data: responseData }, message);
  }

  /**
   * Handles resource fetching requests from the Vue app.
   * Simply re-initializes resources with the requested locale and sends them back using Init message.
   * @param message - The message containing countryId and locale
   */
  async handleFetchResources(message) {
    try {
      LoggerProvider.info(this, 'Handling fetch resources request from Vue', message);
      
      // Support both formats: message.data.locale and message.locale
      const requestedLocale = (message && message.data && message.data.locale) || message.locale;
      
      // Validate required parameters
      if (!requestedLocale) {
        LoggerProvider.error(this, LoggerEvent.Vue, 'Missing required locale parameter');
        return;
      }
      
      // Update the current language if different
      const previousLocale = HttpProvider.currentLanguage;
      if (requestedLocale !== previousLocale) {
        HttpProvider.currentLanguage = requestedLocale;
        this.translate.use(requestedLocale);
      }
      
      // Force reload resources with the new locale
      await this.resourceProvider.initializeResources(true, true);
      
      // Get the refreshed resources
      let resources = await this.resourceProvider.getResources();
      if (!resources) {
        LoggerProvider.info(this, 'Resources not available yet, using fallback');
        resources = this.resourceProvider.resources || {};
      }
      
      // Get features refreshed
      this.utilsProvider.refreshCountryFeatures();
      
      // Prepare the data in the same format as Init
      const initData = {
        countryId: APP_CONFIG.region.regionId,
        locale: requestedLocale,
        session: this.getInitSession(),
        resources: resources || {},
        version: this.version,
        rmt5Flags: this.getRmt5Flags(),
        liquidGlassBarAvailable: await this.nativeProvider.liquidGlassBar.isBarAvailable(),
      };
      
      LoggerProvider.info(this, 'Sending refreshed resources back to Vue', {
        countryId: initData.countryId,
        locale: initData.locale,
        hasResources: !!resources,
        resourceKeys: resources ? Object.keys(resources) : []
      });
      
      // Use Init message type for consistency and compatibility
      this.postMessage(BridgeMessageFromType.Init, { data: initData });
      
      // Restore previous locale if it was changed temporarily
      if (requestedLocale !== previousLocale) {
        HttpProvider.currentLanguage = previousLocale;
        this.translate.use(previousLocale);
      }
      
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Error handling fetch resources request');
    }
  }

  // System back still fires on the onboarding screens even though they draw no control,
  // and router.replace leaves RMT5 nothing to pop but the pre-login landing page.
  consumeHardwareBackDuringOnboarding(): boolean {
    if (!this.pendingOnboardingSignin) {
      return false;
    }
    LoggerProvider.info(this, 'Hardware back during RMT5 onboarding - finalizing login natively');
    this.finalizeOnboarding({ ranByRmt5: false }).catch((ex) =>
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Hardware back onboarding fallback failed'));
    return true;
  }

  /**
   * Handles the close iframe message sent from the Vue app when user exits registration.
   * This occurs when the user clicks the X button in the Vue registration dialog.
   * @param message - The message data from Vue app
   */
  async handleCloseIframe(message) {
    // Already authenticated, so the isLoggedIn() guard below would skip navigation entirely.
    if (this.pendingOnboardingSignin) {
      LoggerProvider.info(this, 'Close during RMT5 onboarding - finalizing login natively');
      await this.finalizeOnboarding({ ranByRmt5: false });
      return;
    }
    try {
      LoggerProvider.info(this, 'Handling close iframe request from Vue app', message.data);
      
      // Hide the Vue bridge iframe
      await this.hideVueBridgeIframe(TransitionType.SLIDE_ANIMATION);
      
      // Clear any pending navigation promises
      if (this.navigatePromise) {
        this.navigateResolve(null);
        this.navigatePromise = null;
      }
      
      // Clear any transition promises
      if (this.transitionPromise) {
        this.transitionResolve(false);
        this.transitionPromise = null;
      }

      // Track analytics event for registration exit
      this.analytics.trackEvent({
        eventName: AnalyticsEventType.RegistrationFailed,
        analyticsData: {
          location: 'vue_bridge',
          method: 'user_close',
          reason: 'user_cancelled'
        }
      });
      
      // If we have auth provider, ensure we're on the pre-login page
      if (this.authProvider && !AuthProvider.isLoggedIn()) {
        // Small delay to ensure smooth transition
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Navigate back to pre-login stories if not already there
        const currentPageName = await this.routeProvider.getCurrentPageName();
        if (currentPageName !== 'PreLoginStoriesPage') {
          await this.routeProvider.setRoot({ page: 'PreLoginStoriesPage' });
        }
      }
      
      LoggerProvider.info(this, 'Vue bridge closed successfully');
      
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Error handling close iframe request');
    }
  }

  /**
   * Handles the open partner checkout message sent from the Vue app (e.g. Ding mobile top-up).
   * Replies with PartnerCheckoutResult on the request's uid once the checkout returns or is closed.
   * @param message - The message data from Vue app containing the BridgePartnerCheckoutRequest
   */
  async handleOpenPartnerCheckout(message) {
    const request: BridgePartnerCheckoutRequest = message.data || {};
    let result;
    try {
      result = await this.partnerCheckoutProvider.open(request);
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Error handling open partner checkout request');
      result = { status: 'failed', params: {} };
    }
    this.postMessage(BridgeMessageFromType.PartnerCheckoutResult, { data: result }, message);
  }

  /**
   * Handles the open external URL message sent from the Vue app when a user clicks on a link.
   * This opens the URL in a modal that can be closed without affecting the iframe.
   * @param message - The message data from Vue app containing the URL to open
   */
  async handleOpenExternalUrl(message) {
    try {
      LoggerProvider.info(this, 'Handling open external URL request from Vue app', message.data);

      if (!message.data || !message.data.url) {
        LoggerProvider.error(this, LoggerEvent.Vue, 'No URL provided in open external URL request');
        return;
      }

      const url = message.data.url;

      if (!this.isValidUrl(url)) {
        LoggerProvider.error(this, LoggerEvent.Vue, 'Invalid URL provided:', url);
        return;
      }

      await this.routeProvider.openIab(url, null, true, true);

      this.analytics.trackEvent({
        eventName: 'External Link Clicked',
        analyticsData: {
          url: url,
          source: 'vue_bridge',
          location: message.data.location || 'unknown'
        }
      });

    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Error handling open external URL request');
    }
  }

  async handleOpenDebugMenu() {
    if (APP_CONFIG.env === Env.PROD || this.debugMenuOpen) {
      return;
    }
    this.debugMenuOpen = true;
    LoggerProvider.info(this, 'Opening debug menu from Vue bridge');
    try {
      const modal = this.modalCtrl.create('DebugMenuPage', {}, { cssClass: 'debug-menu-modal' });
      modal.onDidDismiss(async (result) => {
        this.debugMenuOpen = false;
        try {
          if (result && result.rmt5OverridesChanged) {
            await this.reloadBridgeAfterFlagChange(true);
            // If the change disabled the bridge (e.g. RMT5 Legacy mode), the Vue
            // iframe is torn down — re-root the native prelogin so it shows fresh
            // instead of revealing the stale splash it was hiding behind the iframe
            // (which nothing would hide, leaving the loader stuck).
            if (this.disabled) {
              this.routeProvider.setRoot(this.routeProvider.pages.PreLoginPage, { skipLogout: true, redirected: true }, false);
            }
          }
        } catch (ex) {
          LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Error handling debug menu dismiss');
        }
      });
      await modal.present();
    } catch (ex) {
      this.debugMenuOpen = false;
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Error opening debug menu');
    }
  }

  async reloadBridgeAfterFlagChange(navigateToWelcome: boolean) {
    LoggerProvider.info(this, 'Reloading Vue bridge after feature flag override change');
    LoggerProvider.info(this, 'DebugMenu: reloadBridgeAfterFlagChange entry', `navigateToWelcome=${navigateToWelcome}`);
    this.nativeProvider.liquidGlassBar.reset();
    this.hideVueBridgeIframe(TransitionType.DESTRUCT);

    const wasDisabled = this.disabled;
    const nowDisabled = UtilsProvider.getFeatureFlagDataUnsafe(FeatureFlagTypes.Rmt5VueBridgeDisabled);
    if (nowDisabled) {
      this.disabled = true;
      LoggerProvider.info(this, 'Vue bridge disabled via override, staying on native');
      LoggerProvider.info(this, 'DebugMenu: reloadBridgeAfterFlagChange bailed - bridge disabled via override');
      return;
    }

    this.disabled = false;
    this.navigateDisabled = false;

    if (wasDisabled) {
      await this.init(true);
    }

    if (this.iframe) {
      LoggerProvider.info(this, 'DebugMenu: reloadBridgeAfterFlagChange forcing iframe reload via about:blank');
      this.iframe.src = 'about:blank';
      await this.delay(this.retryDelay);
    }

    await this.setupVueBridgeConnection();
    LoggerProvider.info(this, 'DebugMenu: reloadBridgeAfterFlagChange setupVueBridgeConnection completed', `isConnected=${this.isConnected}`);

    if (navigateToWelcome && this.isAnyWelcomeArmEnabled()) {
      LoggerProvider.info(this, 'DebugMenu: reloadBridgeAfterFlagChange navigating to Welcome');
      await this.navigate('Welcome', '', {} as any, () => {
        VueBridgeState.setUserInsideVueBridge(false);
      }, false);
    } else {
      LoggerProvider.info(this, 'DebugMenu: reloadBridgeAfterFlagChange no Welcome navigation', `navigateToWelcome=${navigateToWelcome} anyWelcomeArm=${this.isAnyWelcomeArmEnabled()}`);
    }
  }

  async handleOpenNativeModal(message) {
    const target = message && message.data && message.data.target;
    const params = (message && message.data && message.data.params) || null;
    LoggerProvider.info(this, `Handling open native modal request: ${target}`);

    this.elevateIframe = false;
    if (this.container) {
      this.container.style.zIndex = '1';
    }

    try {
      switch (target) {
        case BridgeNativeModalTarget.SendMoney:
          this.homescreenProvider.setHomeScreenLocation(SegmentLocationNames.SendMoney);
          this.sendFlowProvider.sendMoneyBtn();
          break;
        case BridgeNativeModalTarget.SendMoneyToRecipient:
          this.handleSendMoneyToRecipient(params);
          break;
        case BridgeNativeModalTarget.Locations:
          this.analytics.trackEvent({
            eventName: AnalyticsEventType.FindALocationAccessed,
            analyticsData: { location: 'vue_bridge', embedded: false },
          });
          this.homescreenProvider.setHomeScreenLocation(SegmentLocationNames.FindALocation);
          if (APP_CONFIG.featureFlags.findLocationRevamp) {
            this.routeProvider.openModal(this.routeProvider.pages.StoresPageNew, params, null, 'store-locator-modal');
          } else {
            this.routeProvider.openModal(this.routeProvider.pages.StoresPage, params, null, 'store-locator-modal');
          }
          break;
        case BridgeNativeModalTarget.Track:
          this.analytics.trackEvent({
            eventName: AnalyticsEventType.TrackTransferAccessed,
            analyticsData: { location: 'vue_bridge' },
          });
          this.homescreenProvider.setHomeScreenLocation(SegmentLocationNames.TrackATransfer);
          this.routeProvider.openModal(this.routeProvider.pages.TrackTransferPage, params, null, 'track-transfer-modal');
          break;
        default:
          LoggerProvider.error(this, LoggerEvent.Vue, `Unknown native modal target: ${target}`);
          return;
      }

      this.postMessage(BridgeMessageFromType.OpenNativeModalAck, { data: { target } }, message);
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Error handling open native modal request');
    }
  }

  async handleSendMoneyToRecipient(params: any) {
    const recipientId = params && (params.recipientId != null ? params.recipientId : params.id);
    const lineNumber = params && params.lineNumber != null ? params.lineNumber : null;
    if (recipientId == null) {
      LoggerProvider.error(this, LoggerEvent.Vue, 'SendMoneyToRecipient missing recipientId');
      return;
    }

    if (this.loginProvider.isUserFraudBlocked()) {
      this.utilsProvider.redirectToRestrictedTransfers();
      return;
    }

    this.loadingProvider.show();
    try {
      const recipient = await this.recipientProvider.getRecipient(Number(recipientId));
      this.loadingProvider.hide();

      this.orderProvider.setCurrentOrder(null);
      this.sendFlowProvider.fireRecipientSelectedEvent(recipient, false, SegmentLocationNames.HomeScreen, lineNumber);
      this.routeProvider.openModal(this.routeProvider.pages.CheckPricesPage, {
        recipient,
        sendFromRecipient: true,
      });
    } catch (ex) {
      this.loadingProvider.hide();
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Failed to open recipient from Vue bridge');
      RmtPopupComponent.showException(this, ex, 'Failed to open recipient');
    }
  }

  /**
   * Validates if a URL is safe to open.
   * @param url - The URL to validate
   * @returns true if the URL is valid and safe to open
   */
  private isValidUrl(url: string): boolean {
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  }

  // Add a method to check connection status
  isVueBridgeConnected(): boolean {
    return this.isConnected;
  }
  
  // Check if Vue navigation should be used
  shouldUseVueNavigation(): boolean {
    return this.isConnected && !this.navigateDisabled;
  }

  // Add a method to reconnect if needed
  async reconnect() {
    LoggerProvider.info(this, 'Forcing Vue bridge reconnection');
    this.nativeProvider.liquidGlassBar.reset();
    this.isConnected = false;
    VueBridgeState.setUserInsideVueBridge(false);
    this.connectionAttempts = 0;
    
    // Reset iframe
    if (this.iframe) {
      this.iframe.src = 'about:blank';
    }
    
    // Setup new connection
    await this.setupVueBridgeConnection();
  }

  /**
   * Disconnects the Vue bridge and resets its state.
   * This ensures a fresh start when the bridge is used again.
   */
  disconnectVueBridge() {
    this.bridgeVisible$.next(false);
    LoggerProvider.info(this, 'Disconnecting Vue bridge');
    this.nativeProvider.liquidGlassBar.reset();
    
    // Mark as disconnected
    this.isConnected = false;
    this.origin = null;
    VueBridgeState.setUserInsideVueBridge(false);
    
    // Clear any pending promises
    if (this.readyResolve) {
      this.readyResolve(false);
      this.readyResolve = null;
    }
    this.readyPromise = null;
    
    if (this.navigateResolve) {
      this.navigateResolve(null);
      this.navigateResolve = null;
    }
    this.navigatePromise = null;
    
    if (this.transitionResolve) {
      this.transitionResolve(false);
      this.transitionResolve = null;
    }
    this.transitionPromise = null;
    
    // Reset iframe to blank
    if (this.iframe) {
      this.iframe.src = 'about:blank';
    }
    
    // Hide container
    VueBridgeContainerHelpers.setContainerHidden(this.container);
    
    // Reset connection attempts
    this.connectionAttempts = 0;
    
    // Clear any timeouts
    if (this.connectionTimeoutId) {
      clearTimeout(this.connectionTimeoutId);
      this.connectionTimeoutId = null;
    }
    
    LoggerProvider.info(this, 'Vue bridge disconnected successfully');
  }

  safeParse(jsonString) {
    if (!this.looksLikeJSON(jsonString)) return null; // quick pre-check
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      LoggerProvider.error(this, LoggerEvent.Vue, 'Error Invalid JSON:', e);
      return null; // or a default object
    }
  }

  looksLikeJSON(jsonString) {
    if (typeof jsonString !== "string") return false;
    const s = jsonString.trim();
    return s.startsWith("{") || s.startsWith("[");
    // return /^[\[\{]/.test(str?.trim());
  }
  

  /** Forwards an Adyen 3DS redirect to cordova-plugin-adyen (mirrors send-psp.ts). */
  handleAdyen3DSRedirect(message: any) {
    const request = message && message.data && message.data.request;
    const action = request && request.action;
    const clientKey = (request && request.clientKey) || '';
    const environment = (request && request.environment) || '';

    if (!action || !action.url) {
      this.postMessage(BridgeMessageFromType.NativeError, { data: { error: 'Adyen 3DS: missing action or action.url' } }, message);
      return;
    }

    const adyenPlugin: any = (window as any).AdyenPlugin;
    if (!adyenPlugin || typeof adyenPlugin.handle3DSRedirect !== 'function') {
      this.postMessage(BridgeMessageFromType.NativeError, { data: { error: 'Adyen 3DS: AdyenPlugin unavailable (cordova-plugin-adyen not installed?)' } }, message);
      return;
    }

    const config = { clientKey: clientKey, environment: environment };
    const debugDetails: any = { url: action.url, method: action.method || 'GET', clientKey: !!clientKey };
    LoggerProvider.info(this, 'Adyen 3DS: forwarding handle3DSRedirect to cordova-plugin-adyen', debugDetails);

    adyenPlugin.handle3DSRedirect(
      (response: any) => {
        const iosResult = response && response.redirectResult;
        const androidResult = response && response.details && response.details.redirectResult;
        const redirectResult = iosResult || androidResult;
        if (!redirectResult) {
          LoggerProvider.error(this, LoggerEvent.PageException, 'Adyen 3DS: no redirectResult in response');
          this.postMessage(BridgeMessageFromType.NativeError, { data: { error: 'Adyen 3DS: no redirectResult in response' } }, message);
          return;
        }
        this.postMessage(BridgeMessageFromType.NativeFinished, { data: { redirectResult: redirectResult } }, message);
      },
      (error: any) => {
        const errorMessage = (error && error.error) || (typeof error === 'string' ? error : 'Adyen 3DS: redirect failed');
        LoggerProvider.error(this, LoggerEvent.PageException, 'Adyen 3DS: ' + errorMessage);
        this.postMessage(BridgeMessageFromType.NativeError, { data: { error: errorMessage } }, message);
      },
      config,
      action,
    );
  }

  async handleBiometricsEnable(message: any) {
    try {
      // RMT5 nests the payload under message.data.request
      const request = (message && message.data && (message.data.request || message.data)) || {};
      const password = request.password || '';
      const canShowFeedBack = !request.silent;
      const result: any = await this.authProvider.enableBiometrics(this.translate, password, canShowFeedBack);
      const enabled = !!(result && result.enabled);
      if (enabled) {
        // FaceID only (OM-11025)
        if (this.authProvider.faceId)
          this.authProvider.scanBiometrics();
      }
      this.postMessage(
        BridgeMessageFromType.NativeFinished,
        { data: { type: BridgeNativeType.Biometrics.Enable, enabled, error: result && result.error, faceId: this.authProvider.faceId, touchId: this.authProvider.touchId } },
        message
      );
    } catch (ex) {
      const err: any = ex;
      LoggerProvider.exception(this, err, LoggerEvent.Vue, 'Biometrics enable failed');
      this.postMessage(
        BridgeMessageFromType.NativeError,
        { data: { type: BridgeNativeType.Biometrics.Enable, error: (err && err.message) || 'biometrics-enable-failed' } },
        message
      );
    }
  }

  async handleBiometricsSkip(message: any) {
    try {
      const method = this.authProvider.faceId ? 'faceID' : 'touchID';
      const location = (message && message.data && message.data.location) || 'VueBridge';
      this.analytics.trackEvent({
        eventName: AnalyticsEventType.BiometricsSetupCompleted,
        analyticsData: {
          biometricsMethod: method,
          biometricsStatus: 'disabled',
          failureReason: null,
          location,
          registrationFlowType: this.utilsProvider.getRegistrationFlowType()
        }
      });
      this.postMessage(
        BridgeMessageFromType.NativeFinished,
        { data: { type: BridgeNativeType.Biometrics.Skip, skipped: true } },
        message
      );
    } catch (ex) {
      const err: any = ex;
      LoggerProvider.exception(this, err, LoggerEvent.Vue, 'Biometrics skip failed');
      this.postMessage(
        BridgeMessageFromType.NativeError,
        { data: { type: BridgeNativeType.Biometrics.Skip, error: (err && err.message) || 'biometrics-skip-failed' } },
        message
      );
    }
  }

  /** Device contacts for RMT5 P2P sync. Never log contact PII. */
  async handleContacts(message: any) {
    const subtype = message.data.type;

    // RMT5 reason -> ContactsProvider reason
    const reasonMap = {
      'p2pImportRecipients': this.contactsProvider.reasons.syncContacts,
    };
    const request = (message && message.data && message.data.request) || {};
    const reason = reasonMap[request.reason] || this.contactsProvider.reasons.syncContacts;

    if (subtype === BridgeNativeType.Contacts.CheckPermission) {
      const status = await this.contactsProvider.getConsentStatus(reason);
      this.postMessage(BridgeMessageFromType.NativeFinished, { data: { status } }, message);
      return;
    }

    try {
      const result: any = await this.contactsProvider.findContacts(
        reason,
        ['displayName', 'phoneNumbers'],
        { hasPhoneNumber: true, multiple: true }
      );

      // false = consent modal rejected
      if (result === false) {
        this.postMessage(BridgeMessageFromType.NativeError, { data: { error: 'denied' } }, message);
        return;
      }

      const rawContacts = result || [];
      const contacts = [];
      for (const c of rawContacts) {
        const name = c && c.name;
        const displayName = (c && c.displayName) ||
          [name && name.givenName, name && name.familyName].filter(Boolean).join(' ').trim();
        const phoneNumbers = ((c && c.phoneNumbers) || [])
          .filter(p => p && p.type === 'mobile' && p.value)
          .map(p => p.value);
        if (phoneNumbers.length) {
          contacts.push({ displayName, phoneNumbers });
        }
      }

      LoggerProvider.info(this, 'Vue bridge contacts-get returned contacts', { count: contacts.length });
      this.postMessage(BridgeMessageFromType.NativeFinished, { data: { contacts } }, message);
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'contacts-get failed');
      const err: any = ex;
      // OS permission denial rejects; treat it as denied
      const text = ((err && err.message) || (err && err.toString && err.toString()) || '').toLowerCase();
      const isDenied = text.indexOf('denied') !== -1 || text.indexOf('permission') !== -1;
      const errorPayload = isDenied ? 'denied' : ((err && err.message) || 'contacts-get failed');
      this.postMessage(BridgeMessageFromType.NativeError, { data: { error: errorPayload } }, message);
    }
  }

  handleRouteChange() {
    if (this.autoHide && VueBridgeContainerHelpers.isContainerVisible(this.container)) {
      LoggerProvider.info(this, 'Route change detected, auto-hiding Vue bridge after transition');
      this.routeProvider.waitForTransitions().then(() => {
        LoggerProvider.info(this, 'Transition complete, auto-hiding Vue bridge now');
        this.hideVueBridgeIframe(TransitionType.DESTRUCT);
      });
    }
    return true
  }

  private handleSendMoneyDismissed() {
    LoggerProvider.info(this, 'Send Money modal dismissed — restoring Vue bridge');
    document.body.classList.remove('vue-bridge-send-money');
    this.bridgeVisible$.next(true);
    if (this.isSendMoneyFromBridge) {
      this.returnToBridgeFromSendMoney();
    }
  }

  // generated PDFs by filename; a POST with the same body reuses the file until the screen discards it
  private sharePdfCache = new Map<string, { key: string; nativeUrl: string }>();

  /** Forgets a generated PDF and deletes its cache file; always replies finished. */
  async handleDiscardPdf(message: any) {
    const request = message && message.data && message.data.request;
    const filename = request && request.filename;
    if (filename) {
      this.sharePdfCache.delete(filename);
      await this.file.removeFile(this.file.cacheDirectory, filename).catch(() => undefined);
    }
    this.postMessage(BridgeMessageFromType.NativeFinished, { data: { success: true } }, message);
  }

  /** Generates a PDF via POST into the cache directory; an identical body reuses the cached file. */
  private async generatePdf(fullUrl: string, body: any, filename: string, mimeType: string): Promise<string> {
    const cacheKey = `${fullUrl}|${JSON.stringify(body)}`;
    const cached = this.sharePdfCache.get(filename);
    const stillOnDisk = cached && cached.key === cacheKey
      && await this.file.checkFile(this.file.cacheDirectory, filename).catch(() => false);
    if (stillOnDisk) {
      return cached.nativeUrl;
    }
    const bytes = await this.httpProvider.postBinary(fullUrl, body, mimeType);
    if (!bytes || !bytes.byteLength) {
      throw new Error('Document request returned no file');
    }
    const entry: any = await this.file.writeFile(this.file.cacheDirectory, filename, bytes, { replace: true });
    const nativeUrl = (entry && entry.nativeURL) || this.file.cacheDirectory + filename;
    this.sharePdfCache.set(filename, { key: cacheKey, nativeUrl });
    return nativeUrl;
  }

  /** Downloads a PDF via GET into the cache directory. */
  private async downloadPdf(fullUrl: string, queryParams: any, filename: string, mimeType: string): Promise<string> {
    const savePath = this.file.cacheDirectory + filename;
    const fileEntry: any = await this.httpProvider.downloadFile(fullUrl, queryParams, savePath, mimeType);
    if (!fileEntry) {
      throw new Error('Download returned no file');
    }
    return (fileEntry && fileEntry.nativeURL) || (fileEntry && typeof fileEntry.toURL === 'function' ? fileEntry.toURL() : savePath);
  }

  /** Downloads (GET) or generates (POST) a PDF from APP_CONFIG.API_URL and presents it with file-opener2 or the share sheet. */
  async handleSharePdf(message: any) {
    const request = (message && message.data && message.data.request) || {};
    // prefetch generates and caches only; a later request with the same body presents the cached file
    const { url: relativeUrl, queryParams = {}, body = {}, share = false, prefetch = false, filename = 'document.pdf', mimeType = 'application/pdf' } = request;
    const method = (request.method || 'GET').toUpperCase();

    if (!relativeUrl) {
      this.postMessage(BridgeMessageFromType.NativeError, { data: { error: 'SharePdf: missing url' } }, message);
      return;
    }

    try {
      // same URL composition as handleHttp
      const fullUrl = `${APP_CONFIG.API_URL}api/${relativeUrl}`;
      if (method !== 'POST') {
        await this.fileOpener.open(await this.downloadPdf(fullUrl, queryParams, filename, mimeType), mimeType);
        this.postMessage(BridgeMessageFromType.NativeFinished, { data: { success: true } }, message);
        return;
      }
      const nativeUrl = await this.generatePdf(fullUrl, body, filename, mimeType);
      // reply before presenting: the viewer or the sheet backgrounds the app and suspends the
      // webview, so a reply sent afterwards would sit behind the bridge timeout
      this.postMessage(BridgeMessageFromType.NativeFinished, { data: { success: true } }, message);
      if (prefetch) {
        return;
      }
      const present = share
        ? this.socialSharing.shareWithOptions({ files: [nativeUrl] })
        : this.fileOpener.open(nativeUrl, mimeType);
      present.catch((ex) => LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'SharePdf present failed'));
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'SharePdf failed');
      const err: any = ex;
      const errorMessage = (err && err.message) || 'SharePdf failed';
      this.postMessage(BridgeMessageFromType.NativeError, { data: { error: errorMessage } }, message);
    }
  }

  async handleTransferToOneApp(message: any) {
    const payload = (message && message.data) || {};
    LoggerProvider.info(this, 'Handling transferToOneApp request from Vue', payload);
    this.loadingProvider.show();

    const requestedPage: string = payload && payload.page;
    let page = this.routeProvider.pages.TabsHomeActivityPage;
    if (this.isWalletRouteBlocked(requestedPage)) {
      LoggerProvider.warning(this, LoggerEvent.Vue, `transferToOneApp: blocked wallet page "${requestedPage}" — no wallet is enabled`);
    } else {
      const targetPage = requestedPage ? (this.routeProvider.pages as any)[requestedPage] : null;
      if (targetPage) {
        page = targetPage;
      } else if (requestedPage) {
        LoggerProvider.error(this, LoggerEvent.Vue, `transferToOneApp: unknown page "${requestedPage}" — falling back to TabsHomeActivityPage`);
      }
    }

    // navigate first; tear the bridge down only once a native page mounts
    await this.routeProvider.setRoot(page, payload.data || {});
    const mounted = await this.nativePageMounted();
    if (mounted) {
      await this.hideVueBridgeIframe(TransitionType.DESTRUCT);
    } else {
      LoggerProvider.error(this, LoggerEvent.Vue, `transferToOneApp: native page "${page.page}" did not mount — keeping the bridge on screen`);
    }
    this.loadingProvider.hide();
  }

  // setRoot can resolve early or bail silently; confirm an ion-page exists
  private async nativePageMounted(timeoutMs: number = 4000): Promise<boolean> {
    try {
      await this.routeProvider.waitForTransitions();
    } catch (ex) {
      LoggerProvider.warning(this, LoggerEvent.Vue, 'waitForTransitions failed while confirming native page', ex as any);
    }
    const deadline = timeoutMs / 100;
    for (let i = 0; i < deadline; i++) {
      // Ionic 3 pages render as <page-foo class="ion-page">
      if (document.querySelectorAll('.ion-page').length > 0) {
        return true;
      }
      await new Promise<void>((resolve) => setTimeout(resolve, 100));
    }
    return false;
  }

  // block Wallet* bridge routes unless the US pilot or regional wallet is live
  private isWalletRouteBlocked(routeName: string): boolean {
    return !!routeName && routeName.indexOf('Wallet') === 0
      && !this.walletProvider.isWalletSupportEnabled()
      && !this.walletProvider.isEnabled();
  }

  static joinUrl(base: string, path: string): string {
    const left = (base || '').replace(/\/+$/, '');
    const right = (path || '').replace(/^\/+/, '');
    return `${left}/${right}`;
  }

  async navigateWithFreshSession(routeName: string, backPage = '', data: VueNavigateData = {}, fallbackCallback: any = null, showLoading: boolean = false) {
    this.navigateDisabled = false; // Ensure navigation is enabled before attempting
    this.loadingProvider.show();
    try {
      await this.httpProvider.refresh(false, false);
    } catch (ex) {
      LoggerProvider.warning(this, LoggerEvent.Vue, `Token refresh before navigate(${routeName}) failed — navigating with current token`, ex as any);
    } finally{
      this.loadingProvider.hide();
    }
    return this.navigate(routeName, backPage, data, fallbackCallback, showLoading);
  }

  async openEkycVerificationFlow(payload: any = {}) {
    LoggerProvider.info(this, 'Opening native eKYC flow from one app', payload);

    // delayed so a fast lookup shows no loader flash
    this.loadingProvider.showDelayed();
    const isSignicatEnabled = (await this.loginProvider.hasMultipleEkycProviders()) && this.loginProvider.isSignicatCountry();

    const params: any = {
      analyticsLocation: (payload && payload.source) || 'VueBridge',
      showBack: true,
      showHelp: false,
      // expired-document re-verify lands here too; derive onboarding like onBack
      usWalletFlow: !this.walletProvider.hasWallet(),
      onGoHome: async () => {
        this.loadingProvider.show();
        this.walletProvider.onboardingDismissed = true;
        try {
          // pop first: DeepLinker's Location.back() would undo an earlier navigate
          this.routeProvider.popOnRoot();
          await this.routeProvider.waitForTransitions();
          this.navigate('RMT5Home').catch((ex) =>
            LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Bridge eKYC go-home navigation failed')
          );
          await this.transitionPromise;
        } catch (ex) {
          LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Bridge eKYC go-home teardown failed');
        }
        this.loadingProvider.hide();
      },
      onBack: async () => {
        if (!this.walletProvider.isWalletSupportEnabled()) {
          this.routeProvider.popOnRoot();
          return;
        }
        const hasActiveWallet = this.walletProvider.hasWallet();
        const targetVuePage = hasActiveWallet ? 'WalletHome' : 'WalletOnboarding';
        LoggerProvider.info(this, `Bridge eKYC back -> Vue ${targetVuePage} (hasActiveWallet=${hasActiveWallet})`);
        this.loadingProvider.show();
        try {
          this.routeProvider.popOnRoot();
          await this.routeProvider.waitForTransitions();
          this.navigate(targetVuePage).catch((ex) =>
            LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Bridge eKYC back navigation failed')
          );
          await this.transitionPromise;
        } catch (ex) {
          LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Bridge eKYC back teardown failed');
        }
        this.loadingProvider.hide();
      },
      onSuccess: async () => {
        LoggerProvider.info(this, `Bridge eKYC FinishPage primary action (isConnected=${this.isConnected}, walletSupportEnabled=${this.walletProvider.isWalletSupportEnabled()})`);

        try {
          // WalletLoading is the only loader here: route the iframe to it first (PushConfirm
          // slides it in over the finish page), drop the native page once it is covered, then
          // refresh native state behind it. The Vue funnel fetches its own profile, so it
          // does not wait on that refresh.
          // navigate() only resolves once vue exits the bridge, so wait on the push confirm instead
          this.bridgeCoverPromise = null;
          this.navigate('WalletEkycSuccess').catch((ex) =>
            LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Bridge eKYC success navigation failed')
          );
          if (!(await this.transitionPromise)) {
            throw new Error('WalletEkycSuccess push was not confirmed');
          }
          if (this.bridgeCoverPromise) {
            await this.bridgeCoverPromise;
          }
          this.routeProvider.popOnRoot();
          await this.routeProvider.waitForTransitions();

          try {
            await this.httpProvider.refresh(false, true, RefreshUserDetails.Full);
            await this.loginProvider.infoClient();
            await this.resourceProvider.initializeResources(true, true);
          } catch (ex) {
            // the bridge already covers the app and vue fetches its own profile, so just log it
            LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Bridge eKYC onSuccess native refresh failed');
          }
        } catch (ex) {
          LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Bridge eKYC onSuccess → WalletEkycSuccess failed');
          this.loadingProvider.hide();
          await this.hideVueBridgeIframe(TransitionType.DESTRUCT);
          this.routeProvider.setRoot(this.routeProvider.pages.TabsHomeActivityPage, {}, false);
        }
      },
    };

    const targetPage = isSignicatEnabled
      ? this.routeProvider.pages.EkycMethodSelectPage
      : this.routeProvider.pages.OnfidoInfoPage;

    this.loadingProvider.hide();
    // Land the native page first, without animation, so the iframe slide reveals it and
    // not the page that was sitting under the bridge while it was still sliding in.
    await this.routeProvider.pushOnRoot(targetPage, params, false);
    await this.hideVueBridgeIframe(TransitionType.SLIDE_ANIMATION);
  }

  async openRecipientSendMoneyFlow(payload: any = {}) {
    LoggerProvider.info(this, 'Opening Send Money flow (with recipient) from Vue bridge', payload);

    const recipientId = payload && (payload.id);
    if (!recipientId) {
      LoggerProvider.error(this, LoggerEvent.Vue, 'openRecipientSendMoneyFlow: missing recipientId in payload');
      return;
    }

    this.loadingProvider.show();
    this.isSendMoneyFromBridge = true;
    this.sendMoneyReturnRoute = (payload && payload.returnRoute) || 'WalletHome';

    document.body.classList.add('vue-bridge-send-money');
    this.bridgeVisible$.next(false);
    this.postMessage(BridgeMessageFromType.Hiding, {});

    let recipient: any = null;
    try {
      recipient = await this.recipientProvider.getRecipient(Number(recipientId));
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, `Failed to fetch recipient ${recipientId} for openRecipientSendMoneyFlow`);
    }

    await this.openSendMoney(recipient);
    this.loadingProvider.hide();
  }

  async openSendMoneyFlow(payload: any = {}) {
    LoggerProvider.info(this, 'Opening Send Money flow from Vue bridge', payload);

    this.loadingProvider.show();
    this.isSendMoneyFromBridge = true;
    this.sendMoneyReturnRoute = (payload && payload.returnRoute) || 'WalletHome';

    document.body.classList.add('vue-bridge-send-money');
    this.bridgeVisible$.next(false);
    this.postMessage(BridgeMessageFromType.Hiding, {});

    await this.openSendMoney();
    this.loadingProvider.hide();
  }

  async returnToBridgeFromSendMoney(): Promise<boolean> {
    if (!this.isSendMoneyFromBridge) return false;

    LoggerProvider.info(this, `Returning to Vue bridge from Send Money flow → ${this.sendMoneyReturnRoute}`);
    const route = this.sendMoneyReturnRoute || 'WalletHome';
    this.isSendMoneyFromBridge = false;
    this.sendMoneyReturnRoute = 'WalletHome';

    await this.navigate(route);
    return true;
  }


  async openSendMoney(recipient: any = null) {
    try {
      const storedOrder = await this.orderProvider.getStoredOrder();
      if (storedOrder && storedOrder.activityDetails && storedOrder.activityDetails.page) {
        if (storedOrder.pendingEKyc || storedOrder.onfidoId) {
          this.loadingProvider.show();
          try {
            this.onfidoProvider.checkId = storedOrder.onfidoId;
            const res = await this.onfidoProvider.getOnfidoStatus(false);
            this.onfidoProvider.handleResult(res, storedOrder, true);
            this.loadingProvider.hide();
            return;
          } catch (ex) {
            LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to get Onfido status in openSendMoney');
            this.loadingProvider.hide();
          }
        }
        await this.routeProvider.openModal(
          storedOrder.activityDetails.page,
          recipient ? { recipient, sendFromRecipient: true } : {},
          () => this.handleSendMoneyDismissed()
        );
        return;
      }
      await this.routeProvider.openModal(
        this.routeProvider.pages.CheckPricesPage,
        recipient ? { recipient, sendFromRecipient: true } : {},
        () => this.handleSendMoneyDismissed()
      );
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'openSendMoney failed');
    }
  }

}


// WEBPACK FOOTER //
// ./src/providers/vue/vue.ts